/* OrgRepo file browser, News feed, Reports, Architecture page */
import React from 'react';
import { Icon, Avatar, EntityLink } from './primitives.jsx';
import { InteractiveChart } from './interactive-chart.jsx';
import { StaffingFence } from './staffing-fence.jsx';
import { getOrgRepoTree, getOrgRepoMetrics, getOrgRepoFile, putOrgRepoFile, deleteOrgRepoFile, analyzeStrategyCascade, applyChangeProposal, dismissChangeProposal, listFirmFeed, listMyArtifacts, deleteMyArtifact, clearAllMyArtifacts, listMyGeneratedPdfs, listMyRuns, closeProject, extendProject, draftProjectReference } from '../api.js';

/* ── ProjectLifecycleFence ──────────────────────────────────────────
 * Renders the ```project-lifecycle``` card the Project Reference Creator
 * Agent posts when a project passes its end date. Buttons hit dedicated
 * endpoints (no chat-model round-trip): close → finished, extend → push
 * end date, draft reference → /projects/<slug>.md in OrgRepo. */
export const ProjectLifecycleFence = ({ spec }) => {
  const { projectId, projectSlug, projectName, clientName, endDate } = spec || {};
  // The card can arrive already "closed" (project finished manually) — it
  // then jumps straight to the upload-docs + draft-reference step.
  const [phase, setPhase] = React.useState(spec?.phase === 'closed' ? 'closed' : 'ask');  // ask | extended | closed | drafted
  const [busy, setBusy] = React.useState(false);
  const [error, setError] = React.useState(null);
  const [note, setNote] = React.useState(null);
  const handle = projectSlug || projectId;
  if (!handle) return null;

  const run = async fn => { setBusy(true); setError(null); try { await fn(); } catch (e) { setError(e.message); } finally { setBusy(false); } };
  const close = () => run(async () => { await closeProject(handle); setPhase('closed'); });
  const extend = weeks => run(async () => {
    const r = await extendProject(handle, weeks);
    setNote(r.endsAt ? `New end date: ${new Date(r.endsAt).toISOString().slice(0, 10)}.` : null);
    setPhase('extended');
  });
  const draft = () => run(async () => { await draftProjectReference(handle); setPhase('drafted'); });

  const wrap = children => (
    <div style={{margin:'12px 0',padding:14,border:'1px solid var(--border)',borderRadius:10,background:'var(--bg-alt)'}}>
      <div style={{fontSize:11,letterSpacing:'0.08em',textTransform:'uppercase',color:'var(--orange)',marginBottom:8,fontWeight:600}}>Project close-out</div>
      <div style={{fontWeight:600,marginBottom:2}}>
        <EntityLink type="project" handle={handle} muted title={`Open ${projectName}`}>{projectName}</EntityLink>{clientName ? ` · ${clientName}` : ''}
      </div>
      {endDate && <div style={{fontSize:12,color:'var(--fg-muted)',marginBottom:10}}>Planned end: {endDate}</div>}
      {error && <div style={{padding:8,marginBottom:8,background:'#FCE6E0',color:'#A20235',borderRadius:6,fontSize:12}}>{error}</div>}
      {children}
    </div>
  );
  const btn = (label, onClick, primary) => (
    <button type="button" className={primary ? 'btn-orange' : 'btn-ghost'} disabled={busy} onClick={onClick} style={{padding:'5px 12px',fontSize:12.5}}>{label}</button>
  );

  if (phase === 'extended') return wrap(<div style={{fontSize:13}}>{note ? note + ' ' : ''}I'll check in again once the new end date passes.</div>);

  if (phase === 'closed' || phase === 'drafted') return wrap(
    <div style={{display:'flex',flexDirection:'column',gap:10}}>
      <div style={{fontSize:13}}>Marked <strong>finished</strong>. Upload the final documents on the <a href={`#projects/${handle}`} style={{color:'var(--orange)'}}>project page</a>, then draft the reference.</div>
      {phase === 'drafted'
        ? <div style={{fontSize:13,color:'var(--support-green)'}}>Reference drafted at <code>/projects/{projectSlug}.md</code> — open <a href="#orgrepo" style={{color:'var(--orange)'}}>OrgRepo</a> to review &amp; refine it.</div>
        : <div>{btn(busy ? 'Drafting…' : 'Draft project reference', draft, true)}</div>}
    </div>
  );

  return wrap(
    <div style={{display:'flex',flexWrap:'wrap',gap:8}}>
      {btn('Yes — close the project', close, true)}
      {btn('Extend +1 week', () => extend(1))}
      {btn('+2 weeks', () => extend(2))}
      {btn('+3 weeks', () => extend(3))}
      {btn('+4 weeks', () => extend(4))}
    </div>
  );
};

/* ── MermaidFence ───────────────────────────────────────────────────
 * Renders ```mermaid … ``` fences as actual diagrams via the mermaid
 * library, lazy-imported on first mount so the ~600KB payload only
 * loads on pages that actually use diagrams (Onboarding manual). On
 * parse error we fall back to a <pre> with the raw source so the user
 * still sees what was intended.
 */
let mermaidPromise = null;
function loadMermaid() {
  if (!mermaidPromise) {
    mermaidPromise = import('mermaid').then(mod => {
      const m = mod.default;
      m.initialize({
        startOnLoad: false,
        theme: 'base',
        themeVariables: {
          fontFamily: "'Mona Sans', system-ui, sans-serif",
          fontSize: '14px',
          primaryColor: '#FCF8F7',
          primaryTextColor: '#202020',
          primaryBorderColor: '#202020',
          lineColor: '#202020',
          tertiaryColor: '#F3ECE6',
        },
        // SVG text labels (not HTML/foreignObject): mermaid sizes each node box
        // from the real getBBox of the text, so multi-line labels always fit —
        // the foreignObject path mis-measured height and clipped the last line.
        flowchart: { htmlLabels: false, useMaxWidth: true },
        securityLevel: 'strict',
      });
      return m;
    });
  }
  return mermaidPromise;
}
const MermaidFence = React.memo(function MermaidFence({ source }) {
  const ref = React.useRef(null);
  const [error, setError] = React.useState(null);
  React.useEffect(() => {
    let cancelled = false;
    loadMermaid()
      .then(async mermaid => {
        if (cancelled) return;
        try {
          // Wait for the brand font before rendering: mermaid measures each node's
          // box from the label's height, so if Mona Sans isn't loaded yet it sizes
          // boxes with a fallback font and the real (taller) text gets clipped at
          // the bottom. Loading the font first makes every box fit its text.
          try {
            if (document.fonts && document.fonts.load) {
              await Promise.all([
                document.fonts.load("14px 'Mona Sans'"),
                document.fonts.load("700 14px 'Mona Sans'"),
              ]);
              await document.fonts.ready;
            }
          } catch { /* font API unavailable — render anyway */ }
          if (cancelled) return;
          const id = 'mermaid-' + Math.random().toString(36).slice(2);
          const { svg } = await mermaid.render(id, source);
          if (cancelled) return;
          if (ref.current) ref.current.innerHTML = svg;
          setError(null);
        } catch (err) {
          if (!cancelled) setError(err.message || String(err));
        }
      })
      .catch(err => { if (!cancelled) setError(err.message || String(err)); });
    return () => { cancelled = true; };
  }, [source]);
  if (error) {
    // Most common cause is a stale cached index.html pointing at a
    // chunk hash that disappeared after the last deploy. Offer a
    // one-tap hard reload so the user can recover without diving
    // into browser settings.
    const looksStale = /import|chunk|404|not found/i.test(String(error));
    return (
      <div style={{padding:'var(--space-3)',background:'var(--bg-alt)',borderRadius:'var(--r-md)',fontSize:'var(--text-xs)',color:'var(--fg-muted)'}}>
        <div style={{display:'flex',justifyContent:'space-between',alignItems:'center',gap:8,marginBottom:'var(--space-2)'}}>
          <span><strong>Diagram couldn't render</strong>{looksStale ? ' — looks like a stale cached asset after a deploy.' : ` (${error}).`}</span>
          <button
            type="button"
            onClick={() => { try { window.location.reload(); } catch {} }}
            style={{padding:'4px 10px',fontSize:11,fontWeight:600,background:'var(--orange)',color:'#fff',border:0,borderRadius:6,cursor:'pointer'}}
          >Reload page</button>
        </div>
        <pre style={{whiteSpace:'pre-wrap',margin:0}}>{source}</pre>
      </div>
    );
  }
  return (
    <div
      ref={ref}
      style={{
        margin:'var(--space-5) 0',
        padding:'var(--space-5)',
        background:'var(--bg-alt)',
        borderRadius:'var(--r-lg)',
        overflowX:'auto',
        textAlign:'center',
      }}
    />
  );
});

const FOLDER_ICONS = {
  '/actors': 'profile',
  '/trigger': 'time',
  '/intents': 'sparkle',
  '/skills': 'sparkle',
  '/sources': 'book',
  '/artifacts': 'book',
  '/rules': 'shield',
  '/audit': 'time',
  '/clients': 'briefcase',
};
// Order matches the eight elements of the firm.
const FOLDER_ORDER = [
  '/actors',  '/trigger',   '/intents',   '/skills',
  '/sources', '/artifacts', '/rules',     '/audit',
];

function groupTree(files) {
  const groups = {};
  for (const f of files) {
    const top = '/' + f.path.split('/')[1];
    if (!groups[top]) groups[top] = [];
    groups[top].push(f);
  }
  for (const k of Object.keys(groups)) {
    groups[k].sort((a, b) => a.path.localeCompare(b.path));
  }
  return groups;
}

// The eight elements of the firm, in flow order. Tabs filter the tree by
// path-prefix /<id>/. Rules spans governance+guidelines+standards sub-folders; Actors
// spans roles+agents; Audit is the former /logs.
const ELEMENT_DEFS = [
  { id: 'actors',     label: 'Actors',     icon: 'profile',   color: '#1A1414',
    desc: 'Who acts — Humans (roles) and Agents (system · public · personal), interchangeable. An actor owns 1..n triggers. Sub-folders: /actors/roles, /actors/agents.' },
  { id: 'trigger',    label: 'Trigger',    icon: 'time',      color: '#C8501E',
    desc: 'How a run starts — Frontend (human entry) · Time (schedule) · Event (system/API). An actor owns 1..n triggers; a trigger raises 1..n intents.' },
  { id: 'intents',    label: 'Intent',     icon: 'sparkle',   color: '#FF6428',
    desc: 'The desired outcome — Objective (ongoing aim) or Task (one-off). Combines 1..n skills. Standing or ephemeral (promotable to Standard on admin approval).' },
  { id: 'skills',     label: 'Skill',      icon: 'sparkle',   color: '#715AAE',
    desc: 'A single reusable capability — Input · Synthesize · Output. Each Input skill draws on 1..n sources.' },
  { id: 'sources',    label: 'Sources',    icon: 'book',      color: '#1E7A45',
    desc: 'What skills draw on — Own data · User input · Connected tools · Open knowledge. Standing (registered) or ad-hoc.' },
  { id: 'artifacts',  label: 'Artifacts',  icon: 'book',      color: '#C8501E',
    desc: 'The type/format catalog of everything the firm produces — Documents · Visuals · Messages · Data & writes. Definitions; produced files live in storage + the Artifacts page.' },
  { id: 'rules',      label: 'Rules',      icon: 'shield',    color: '#A20235',
    desc: 'Govern every step — Governance (enforced, blocking) + Guidelines (guiding norms) + Standards (brand & deck design reference). Sub-folders: /rules/governance, /rules/guidelines, /rules/standards.' },
  { id: 'audit',      label: 'Audit',      icon: 'time',      color: '#B83C0F',
    desc: 'How the firm audits itself — the four mechanisms: Logs & records · Auto-documentation · Monitoring & alerts · Evaluation & feedback. (The actual run-traces are instances under /logs, not shown here.)' },
];

// Every element owns exactly the files under /<id>/. Audit holds the DEFINITIONS
// of the firm's four auditing mechanisms (logs & records, auto-documentation,
// monitoring & alerts, evaluation & feedback) — NOT the actual run-traces. Those
// live under /logs/ as run instances (the readable face of the audit_log table),
// the same way a produced PDF is an instance of the `pdf` artifact type, not part
// of the Artifacts element. So Audit spans only /audit/.
const ELEMENT_PREFIXES = {
};
function elementPrefixes(id) {
  return ELEMENT_PREFIXES[id] || [`/${id}/`];
}

// Sub-categories within an element's left file list. Keyed by element id; each
// entry maps a path sub-folder (or virtual bucket) to a friendly label, in the
// order they should appear. Files whose 2nd path segment matches a sub-folder
// group under it; everything else falls into a trailing "Other" group.
// `virtual` sub-categories have no sub-folder on disk — they collect top-level
// files of the element by a keyword match on the filename (so e.g. Rules can
// show an "Access rights" group without a /rules/access/ folder).
const SUBCATEGORIES = {
  actors: [
    { sub: 'humans/functions', label: 'Humans · Functions' },
    { sub: 'humans/roles',     label: 'Humans · Roles' },
    // system-automation.md lives at /actors root (it's a distinct third actor
    // kind — "platform machinery", neither Human nor Agent — see its doc + the
    // _TEMPLATE), but reads naturally alongside the system actors, so we surface
    // it inside this group by name without moving/reclassifying the file.
    { sub: 'agents/system',    label: 'Agents · System', match: /^system-automation/ },
    { sub: 'agents/public',    label: 'Agents · Public' },
    { sub: 'agents/personal',  label: 'Agents · Personal' },
  ],
  rules: [
    { folder: 'governance', label: 'Governance' },
    { folder: 'guidelines', label: 'Guidelines' },
    { folder: 'standards',  label: 'Standards' },
    { virtual: 'access', label: 'Access rights', match: /access|permission|right/i },
  ],
  trigger: [
    { virtual: 'frontend', label: 'Frontend', match: /frontend|chat|button/i },
    { virtual: 'time',     label: 'Time',     match: /(^|[-/])time|cron|schedule/i },
    { virtual: 'event',    label: 'Event',    match: /event|webhook|api/i },
    { virtual: 'other',    label: 'Other',    match: /.*/ },
  ],
  // Intents are NOT divided into objectives/tasks — each intent has its own
  // goal and an Input/Synthesize/Output structure inside its file. Flat list.
  intents: null,
  skills: [
    { virtual: 'input',     label: 'Input',     match: /(^|[-/])(db-|.*-search|web-search|read-|currency|my-insights|eos-platform)/i },
    { virtual: 'output',    label: 'Output',    match: /render|export|draft|chart|cards|table|brief|sow|deck|memo|doc|spreadsheet|frontend-design|design|stylist/i },
    { virtual: 'synthesize',label: 'Synthesize',match: /.*/ },
  ],
  sources: [
    { virtual: 'own',   label: 'Own data',        match: /eos-db|eos-files|orgrepo|^db/i },
    { virtual: 'user',  label: 'User input',      match: /chat-input|upload/i },
    { virtual: 'tool',  label: 'Connected tools', match: /connected|tool/i },
    { virtual: 'open',  label: 'Open knowledge',  match: /web|llm|fx/i },
    { virtual: 'other', label: 'Other',           match: /.*/ },
  ],
  artifacts: [
    { virtual: 'documents', label: 'Documents',     match: /pdf|docx|pptx|xlsx/i },
    { virtual: 'visuals',   label: 'Visuals',       match: /png|jpg|chart|card/i },
    { virtual: 'messages',  label: 'Messages',      match: /message|memo/i },
    { virtual: 'data',      label: 'Data & writes', match: /table|write/i },
    { virtual: 'other',     label: 'Other',         match: /.*/ },
  ],
  audit: [
    { virtual: 'mechanisms', label: 'Auditing mechanisms', match: /.*/ },
  ],
};

// Split an element's files into ordered sub-category groups for the left list.
function groupBySubcategory(elementId, files) {
  const idx = `/${elementId}/index.md`;
  const indexFile = files.find(f => f.path === idx);
  // index.md and the element's master spec (_TEMPLATE.md) are ALWAYS pinned to
  // the very top of the list, in that order, with no sub-category header — they
  // describe the element type itself and must never be swept into a keyword
  // bucket ("Other" / "Synthesize" / "Auditing mechanisms" / …). There is only
  // ever ONE template per element (the canonical one at the element root); any
  // other `_TEMPLATE.md` is a stale legacy-path row in the store, so we pin the
  // canonical one and suppress the rest entirely (never render a duplicate).
  const seen = new Set();
  const pinned = [];
  if (indexFile) { pinned.push(indexFile); seen.add(indexFile.path); }
  const templates = files.filter(f => f.path.split('/').pop() === '_TEMPLATE.md');
  const canonicalTemplate = templates.find(f => f.path === `/${elementId}/_TEMPLATE.md`) || templates[0];
  if (canonicalTemplate) pinned.push(canonicalTemplate);
  for (const t of templates) seen.add(t.path); // suppress every template from the buckets below
  const pinnedHead = pinned.length ? [{ label: null, files: pinned }] : [];

  const subs = SUBCATEGORIES[elementId];
  if (!subs) {
    const flat = files.filter(f => !seen.has(f.path));
    return [...pinnedHead, ...(flat.length ? [{ label: null, files: flat }] : [])];
  }
  const rest = files.filter(f => !seen.has(f.path));
  const groups = subs.map(s => ({ ...s, files: [] }));
  const used = new Set();
  for (const f of rest) {
    const segs = f.path.split('/').filter(Boolean); // [element, maybe-subfolder, ...name]
    let placed = false;
    // 1a) multi-segment sub-path match (e.g. 'humans/functions', 'agents/system')
    for (const g of groups) {
      if (g.sub && f.path.startsWith(`/${elementId}/${g.sub}/`)) { g.files.push(f); placed = true; break; }
    }
    if (placed) { used.add(f.path); continue; }
    // 1b) real sub-folder match (2nd segment)
    for (const g of groups) {
      if (g.folder && segs[1] === g.folder) { g.files.push(f); placed = true; break; }
    }
    if (placed) { used.add(f.path); continue; }
    // 2) keyword match on the filename — first matching group wins. Used by
    // `virtual` buckets (no folder on disk) and by folder groups that also want
    // to adopt a stray root file by name (e.g. system-automation → Agents·System).
    const name = segs[segs.length - 1];
    for (const g of groups) {
      if (g.match && !used.has(f.path) && g.match.test(name)) {
        g.files.push(f); placed = true; used.add(f.path); break;
      }
    }
  }
  const leftover = rest.filter(f => !used.has(f.path));
  const out = [...pinnedHead]; // index.md + _TEMPLATE.md pinned, no header
  for (const g of groups) if (g.files.length) out.push({ label: g.label, files: g.files });
  if (leftover.length) out.push({ label: 'Other', files: leftover });
  return out;
}

/* ════════════════════════════════════════════════════════════════════
 *  OrgRepo — table view + live "company brain"
 *
 *  The default landing for OrgRepo: the whole firm-as-code as one
 *  expandable directory table (eight elements → folders → md files), with a
 *  uniform metric set per row and a green/orange/red health status that
 *  cascades upward, sitting under a live mind-map of the eOS at the centre
 *  with the eight elements (and one level below) fanned around it.
 *  Data: GET /api/orgrepo/metrics (see functions/_lib/orgrepo-metrics.ts).
 * ════════════════════════════════════════════════════════════════════ */

const STATUS_COLOR = { green: '#2C7A4F', orange: '#E8821E', red: '#A20235' };
const STATUS_GLOW  = { green: 'rgba(44,122,79,0.12)', orange: 'rgba(232,130,30,0.16)', red: 'rgba(162,2,53,0.16)' };
const STATUS_LABEL = { green: 'Healthy', orange: 'Watch', red: 'Error' };

const fmtInt = n => (n > 0 ? n.toLocaleString('en-US') : '—');
const fmtKb  = n => (n > 0 ? `${n.toLocaleString('en-US', { maximumFractionDigits: 1 })} KB` : '—');
// Scales KB → MB → GB so storage / file totals stay readable.
const fmtSize = kb => {
  if (!kb || kb <= 0) return '0';
  if (kb < 1024) return `${kb.toLocaleString('en-US', { maximumFractionDigits: 1 })} KB`;
  const mb = kb / 1024;
  if (mb < 1024) return `${mb.toLocaleString('en-US', { maximumFractionDigits: 1 })} MB`;
  return `${(mb / 1024).toLocaleString('en-US', { maximumFractionDigits: 2 })} GB`;
};

const StatusDot = ({ status, size = 9 }) => (
  <span style={{
    display: 'inline-block', flexShrink: 0, width: size, height: size, borderRadius: '50%',
    background: STATUS_COLOR[status] || '#9C8C82',
    boxShadow: `0 0 0 3px ${STATUS_GLOW[status] || 'rgba(120,110,100,0.10)'}`,
  }}/>
);

// Compact "1✕ 2△" rollup of error/watch file counts under a node.
const CountChips = ({ counts }) => {
  if (!counts || (!counts.red && !counts.orange)) return null;
  return (
    <span style={{ fontSize: 10.5, color: 'var(--fg-muted)', fontFamily: 'JetBrains Mono', whiteSpace: 'nowrap' }}>
      {counts.red ? <span style={{ color: STATUS_COLOR.red }}>{counts.red}✕</span> : null}
      {counts.red && counts.orange ? ' ' : ''}
      {counts.orange ? <span style={{ color: STATUS_COLOR.orange }}>{counts.orange}△</span> : null}
    </span>
  );
};

const StatusBadge = ({ node }) => {
  const reasons = node.statusReasons && node.statusReasons.length ? ' — ' + node.statusReasons.join('; ') : '';
  const c = node.counts || { green: 0, orange: 0, red: 0 };
  const tip = `${STATUS_LABEL[node.status]}${reasons}\n${c.green} healthy · ${c.orange} watch · ${c.red} error`;
  return (
    <span title={tip} style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
      <StatusDot status={node.status}/>
      <span style={{ fontSize: 11.5, color: STATUS_COLOR[node.status], fontWeight: 600 }}>{STATUS_LABEL[node.status]}</span>
      <CountChips counts={c}/>
    </span>
  );
};

const NUM_CELL = { textAlign: 'right', fontFamily: 'JetBrains Mono', fontSize: 12, whiteSpace: 'nowrap' };
const MUTED = { color: 'var(--fg-muted)' };

// Own code — the element's own slice (lines · size).
function ownCodeCell(m) {
  if (!m || m.ownLoc <= 0) return '—';
  return (
    <span title={`${fmtInt(m.ownElements)} element${m.ownElements === 1 ? '' : 's'} · ${fmtInt(m.ownLoc)} LOC · ${fmtKb(m.ownKb)}`}>
      {fmtInt(m.ownLoc)} <span style={MUTED}>LOC</span> · {fmtKb(m.ownKb)}
    </span>
  );
}
// Delegates — distinct modules it calls into (count · combined size).
function delegateCell(m) {
  if (!m || m.delegateModules <= 0) return '—';
  return (
    <span title={`${fmtInt(m.delegateModules)} delegate module${m.delegateModules === 1 ? '' : 's'} · ${fmtInt(m.delegateLoc)} LOC · ${fmtKb(m.delegateKb)}`}>
      {fmtInt(m.delegateModules)} <span style={MUTED}>mod</span> · {fmtKb(m.delegateKb)}
    </span>
  );
}
// Files / storage — produced artifact files of a type, or the D1/R2 storage
// total carried on a Source node.
function filesCell(node) {
  if (node.storage) {
    const s = node.storage;
    const primary = s.kb > 0 ? fmtSize(s.kb)
      : (s.count != null ? `${fmtInt(s.count)} ${s.kind === 'd1' ? 'tables' : 'objects'}` : '—');
    return (
      <span title={s.detail} style={{ fontWeight: 600 }}>
        <span style={{ color: s.kind === 'd1' ? '#1E7A45' : '#C8501E' }}>{s.kind === 'd1' ? 'D1' : 'R2'}</span> · {primary}
      </span>
    );
  }
  const m = node.metrics;
  if (m && m.fileCount > 0) {
    return (
      <span title={`${fmtInt(m.fileCount)} produced file${m.fileCount === 1 ? '' : 's'} · ${fmtSize(m.fileKb)}`}>
        {fmtInt(m.fileCount)} <span style={MUTED}>files</span> · {fmtSize(m.fileKb)}
      </span>
    );
  }
  return '—';
}

// Flatten the (recursively expandable) tree into the rows currently visible.
function flattenMetricTree(nodes, expanded, depth, out) {
  for (const n of nodes || []) {
    out.push({ node: n, depth });
    if (n.children && n.children.length && expanded.has(n.path)) {
      flattenMetricTree(n.children, expanded, depth + 1, out);
    }
  }
}
function collectExpandablePaths(nodes, out) {
  for (const n of nodes || []) {
    if (n.children && n.children.length) { out.add(n.path); collectExpandablePaths(n.children, out); }
  }
  return out;
}

const KIND_ICON = { element: 'book', dir: 'book', file: 'file' };

/* ── OrgRepoBrain ─────────────────────────────────────────────────────
 * The live company-brain mind-map, synced with the table. eOS at the centre;
 * the eight elements ring it (colour = status, thickness = 24h calls, flow =
 * active in last 30 min). Each node shows its child count; clicking a node (or
 * the matching table row) expands its children on the next ring and zooms in —
 * the same expansion state drives the table and the graphic, at any depth. */
const OrgRepoBrain = ({ data, expanded, onToggleNode, focus }) => {
  const W = 1000, H = 340, cx = 500, cy = 170;
  const els = (data && data.elements) || [];

  // Radial tree that mirrors the table's expansion: the 8 elements sit on ring 1;
  // an expanded node fans its children onto the next ring within its angular
  // slice. Recurses to any depth (children capped per node so it stays legible).
  const layout = React.useMemo(() => {
    const nodes = [], links = [];
    const ringR = [0, 120, 196, 256, 308, 352];
    const radius = d => ringR[Math.min(d, ringR.length - 1)];
    const n = els.length || 8;
    // rootAct = the owning top-level element's activity, carried down so deeper
    // links pulse/flow when their element is live (lower elements show activity).
    const place = (node, depth, angle, span, parentPos) => {
      const r = radius(depth);
      const x = cx + r * Math.cos(angle), y = cy + r * Math.sin(angle);
      const pos = { x, y };
      const kids = node.children || [];
      nodes.push({ node, x, y, angle, depth, hasKids: kids.length > 0, childCount: kids.length });
      // link carries the CHILD's own activity → only sub-elements that actually
      // ran (e.g. a specific skill) flow, not the whole subtree.
      if (parentPos) links.push({ from: parentPos, to: pos, status: node.status, act: node.activity });
      if (kids.length && expanded && expanded.has(node.path)) {
        const shown = kids.slice(0, 16);
        const m = shown.length;
        const start = angle - span / 2, sub = span / m;
        shown.forEach((kid, j) => {
          const a2 = m === 1 ? angle : start + sub * (j + 0.5);
          place(kid, depth + 1, a2, Math.max(sub * 0.86, 0.04), pos);
        });
      }
    };
    els.forEach((el, i) => {
      const a = (-90 + (i * 360) / n) * Math.PI / 180;
      place(el, 1, a, (2 * Math.PI / n) * 0.92, null);
    });
    return { nodes, links };
  }, [els, expanded]);

  const elementNodes = React.useMemo(() => layout.nodes.filter(nd => nd.depth === 1), [layout]);

  // Content bounding box — the union of every node's position (padded) and the
  // base frame. Panning is clamped to this, so expanded nodes that sit OUTSIDE
  // the base frame are still reachable by dragging.
  const bounds = React.useMemo(() => {
    let nx0 = Infinity, nx1 = -Infinity, ny0 = Infinity, ny1 = -Infinity;
    for (const nd of layout.nodes) { nx0 = Math.min(nx0, nd.x); nx1 = Math.max(nx1, nd.x); ny0 = Math.min(ny0, nd.y); ny1 = Math.max(ny1, nd.y); }
    if (!Number.isFinite(nx0)) { nx0 = 0; nx1 = W; ny0 = 0; ny1 = H; }
    const pad = 90; // room for the labels that sit outside each node
    return { x0: Math.min(0, nx0 - pad), x1: Math.max(W, nx1 + pad), y0: Math.min(0, ny0 - pad), y1: Math.max(H, ny1 + pad) };
  }, [layout]);
  const boundsRef = React.useRef(bounds);
  boundsRef.current = bounds;

  // Busiest spoke (by 24h calls) sets the thickness scale.
  const maxH24 = React.useMemo(() => els.reduce((m, el) => Math.max(m, (el.activity && el.activity.last24h) || 0), 0), [els]);

  // ── pinch / wheel zoom + pan, clipped to the frame ────────────────
  const svgRef = React.useRef(null);
  const zoomGroupRef = React.useRef(null);
  const [view, setView] = React.useState({ k: 1, tx: 0, ty: 0 });
  const [smooth, setSmooth] = React.useState(false);
  const viewRef = React.useRef(view);
  viewRef.current = view;
  const gestureRef = React.useRef(null);

  // Apply a candidate view, clamped: zoom 1×–5×, and pan kept within the content
  // bounds so you can reach every node (including expanded ones outside the base
  // frame) but can't drag the constellation off into empty space.
  // `write` paints the transform straight onto the DOM node (frictionless during
  // a gesture — no React re-render of every node); `commit` syncs React state.
  const applyView = React.useCallback((next, opts = {}) => {
    const { commit = true, write = false } = opts;
    const k = Math.max(1, Math.min(5, next.k));
    const b = boundsRef.current;
    const axLo = W - k * b.x1, axHi = -k * b.x0;
    const ayLo = H - k * b.y1, ayHi = -k * b.y0;
    const tx = Math.min(Math.max(axLo, axHi), Math.max(Math.min(axLo, axHi), next.tx));
    const ty = Math.min(Math.max(ayLo, ayHi), Math.max(Math.min(ayLo, ayHi), next.ty));
    const v = { k, tx, ty };
    viewRef.current = v;
    if (write && zoomGroupRef.current) zoomGroupRef.current.setAttribute('transform', `translate(${tx} ${ty}) scale(${k})`);
    if (commit) setView(v);
  }, [W, H]);

  React.useEffect(() => {
    const svg = svgRef.current;
    if (!svg) return undefined;
    // client px → svg viewport units (accounts for xMidYMid meet letterboxing)
    const toSvg = (clientX, clientY) => {
      const r = svg.getBoundingClientRect();
      const s = Math.min(r.width / W, r.height / H) || 1;
      const offX = (r.width - W * s) / 2, offY = (r.height - H * s) / 2;
      return { x: (clientX - r.left - offX) / s, y: (clientY - r.top - offY) / s };
    };
    const dist = (a, b) => Math.hypot(a.clientX - b.clientX, a.clientY - b.clientY);
    const midpt = (a, b) => ({ x: (a.clientX + b.clientX) / 2, y: (a.clientY + b.clientY) / 2 });

    const onStart = (e) => {
      setSmooth(false); // gestures update continuously — no CSS transition lag
      if (e.touches.length === 2) {
        const m = midpt(e.touches[0], e.touches[1]);
        gestureRef.current = { mode: 'pinch', lastDist: dist(e.touches[0], e.touches[1]), lastMid: toSvg(m.x, m.y) };
        e.preventDefault();
      } else if (e.touches.length === 1 && viewRef.current.k > 1) {
        // record pan, but DON'T preventDefault here — a tap (no move) must still
        // fire the node's click to expand. We preventDefault once it's a drag.
        gestureRef.current = { mode: 'pan', last: toSvg(e.touches[0].clientX, e.touches[0].clientY) };
      } else {
        gestureRef.current = null; // 1 finger at 1× → let the page scroll
      }
    };
    const onMove = (e) => {
      const g = gestureRef.current;
      if (!g) return;
      if (g.mode === 'pinch' && e.touches.length === 2) {
        const d = dist(e.touches[0], e.touches[1]);
        const m = midpt(e.touches[0], e.touches[1]);
        const ms = toSvg(m.x, m.y);
        const v = viewRef.current;
        const kNew = Math.max(1, Math.min(5, v.k * (d / (g.lastDist || d))));
        const ar = kNew / v.k;
        applyView({
          k: kNew,
          tx: ms.x - ar * (ms.x - v.tx) + (ms.x - g.lastMid.x),
          ty: ms.y - ar * (ms.y - v.ty) + (ms.y - g.lastMid.y),
        }, { commit: false, write: true });
        g.lastDist = d; g.lastMid = ms;
        e.preventDefault();
      } else if (g.mode === 'pan' && e.touches.length === 1) {
        const p = toSvg(e.touches[0].clientX, e.touches[0].clientY);
        const v = viewRef.current;
        applyView({ k: v.k, tx: v.tx + (p.x - g.last.x), ty: v.ty + (p.y - g.last.y) }, { commit: false, write: true });
        g.last = p;
        e.preventDefault();
      }
    };
    const onEnd = (e) => {
      if (e.touches.length === 0) {
        gestureRef.current = null;
        applyView(viewRef.current, { commit: true }); // sync React state after the gesture
      } else if (e.touches.length === 1) {
        gestureRef.current = viewRef.current.k > 1
          ? { mode: 'pan', last: toSvg(e.touches[0].clientX, e.touches[0].clientY) }
          : null;
      }
    };
    const onWheel = (e) => {
      e.preventDefault();
      setSmooth(false);
      const p = toSvg(e.clientX, e.clientY);
      const v = viewRef.current;
      const kNew = Math.max(1, Math.min(5, v.k * Math.exp(-e.deltaY * 0.0015)));
      const ar = kNew / v.k;
      applyView({ k: kNew, tx: p.x - ar * (p.x - v.tx), ty: p.y - ar * (p.y - v.ty) }, { commit: true, write: true });
    };
    const onDbl = () => { setSmooth(true); applyView({ k: 1, tx: 0, ty: 0 }); };

    svg.addEventListener('touchstart', onStart, { passive: false });
    svg.addEventListener('touchmove', onMove, { passive: false });
    svg.addEventListener('touchend', onEnd, { passive: false });
    svg.addEventListener('touchcancel', onEnd, { passive: false });
    svg.addEventListener('wheel', onWheel, { passive: false });
    svg.addEventListener('dblclick', onDbl);
    return () => {
      svg.removeEventListener('touchstart', onStart);
      svg.removeEventListener('touchmove', onMove);
      svg.removeEventListener('touchend', onEnd);
      svg.removeEventListener('touchcancel', onEnd);
      svg.removeEventListener('wheel', onWheel);
      svg.removeEventListener('dblclick', onDbl);
    };
  }, [applyView, W, H]);

  // Zoom-to-focus: whenever a node is toggled (from the graphic OR the table),
  // smoothly centre + zoom into it (depth → tighter zoom). A collapsed top
  // element, or an unknown target (e.g. collapse-all), resets to fit.
  React.useEffect(() => {
    if (!focus) return;
    setSmooth(true);
    const self = layout.nodes.find(n2 => n2.node.path === focus.path);
    if (!self) { applyView({ k: 1, tx: 0, ty: 0 }); return; } // collapse-all / reset
    const expandedNow = expanded && expanded.has(focus.path);
    if (expandedNow) {
      // fit the node + its now-visible descendants so the children are in view
      const sub = layout.nodes.filter(n2 => n2.node.path === focus.path || n2.node.path.startsWith(focus.path + '/'));
      let x0 = Infinity, x1 = -Infinity, y0 = Infinity, y1 = -Infinity;
      for (const nd of sub) { x0 = Math.min(x0, nd.x); x1 = Math.max(x1, nd.x); y0 = Math.min(y0, nd.y); y1 = Math.max(y1, nd.y); }
      const pad = 80, bw = (x1 - x0) + 2 * pad, bh = (y1 - y0) + 2 * pad;
      const k = Math.max(1, Math.min(4.5, Math.min(W / bw, H / bh)));
      applyView({ k, tx: W / 2 - k * (x0 + x1) / 2, ty: H / 2 - k * (y0 + y1) / 2 });
    } else if (self.depth === 1) {
      applyView({ k: 1, tx: 0, ty: 0 });
    } else {
      const k = [1, 1.6, 2.2, 2.8, 3.4][Math.min(self.depth, 4)] || 2.4;
      applyView({ k, tx: W / 2 - k * self.x, ty: H / 2 - k * self.y });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [focus]);

  if (!els.length) return null;
  const rootStatus = (data.root && data.root.status) || 'green';

  return (
    <div className="orgrepo-brain">
      <svg ref={svgRef} viewBox={`0 0 ${W} ${H}`} width="100%" height="100%" preserveAspectRatio="xMidYMid meet" role="img"
        style={{ touchAction: view.k > 1 ? 'none' : 'pan-y', display: 'block', cursor: view.k > 1 ? 'grab' : 'default' }}
        aria-label="OrgRepo as a living mind-map: eOS at the centre with the eight elements around it. Pinch or scroll to zoom.">
        <defs>
          <radialGradient id="brainGlow" cx="50%" cy="50%" r="50%">
            <stop offset="0%" stopColor="#FF6428" stopOpacity="0.16"/>
            <stop offset="60%" stopColor="#FF6428" stopOpacity="0.04"/>
            <stop offset="100%" stopColor="#FF6428" stopOpacity="0"/>
          </radialGradient>
        </defs>

        <g ref={zoomGroupRef} transform={`translate(${view.tx} ${view.ty}) scale(${view.k})`} style={{ transition: smooth ? 'transform 0.4s ease' : 'none' }}>
        {/* soft central glow */}
        <circle cx={cx} cy={cy} r={150} fill="url(#brainGlow)"/>

        {/* ring-1 spokes: centre → element. Colour = status (green when healthy),
            thickness = calls in last 24h, flowing pulse = active in last 30 min. */}
        {elementNodes.map((g, i) => {
          const el = g.node;
          const act = el.activity || { last30m: 0, last24h: 0 };
          const flowing = act.last30m > 0;
          const stroke = STATUS_COLOR[el.status] || '#9C8C82';
          const dx = g.x - cx, dy = g.y - cy, len = Math.hypot(dx, dy) || 1, off = 26;
          const ctrlx = (cx + g.x) / 2 + (-dy / len) * off, ctrly = (cy + g.y) / 2 + (dx / len) * off;
          const t = maxH24 > 0 ? Math.log1p(act.last24h) / Math.log1p(maxH24) : 0;
          const width = 0.9 + 3.4 * t;
          return (
            <path key={`l1${i}`} d={`M ${cx} ${cy} Q ${ctrlx} ${ctrly} ${g.x} ${g.y}`}
              fill="none" stroke={stroke} strokeWidth={width} strokeOpacity={0.85} strokeLinecap="round"
              className={flowing ? 'orgrepo-brain__wire orgrepo-brain__wire--live' : 'orgrepo-brain__wire'}
              style={{ animationDelay: `${(i % 5) * 0.5}s` }}>
              <title>{`${el.label} — ${act.detail || 'no recent activity'}`}</title>
            </path>
          );
        })}

        {/* deeper links: parent → child. Colour = child status; they inherit
            their element's live activity — flowing when it's active in the last
            30 min, thicker the busier it was over 24h. */}
        {layout.links.map((lk, i) => {
          const act = lk.act || { last30m: 0, last24h: 0 };
          const flowing = act.last30m > 0;
          const t = maxH24 > 0 ? Math.log1p(act.last24h) / Math.log1p(maxH24) : 0;
          return (
            <line key={`l2${i}`} x1={lk.from.x} y1={lk.from.y} x2={lk.to.x} y2={lk.to.y}
              stroke={STATUS_COLOR[lk.status] || '#9C8C82'} strokeWidth={0.9 + 1.8 * t} strokeOpacity={0.55} strokeLinecap="round"
              className={flowing ? 'orgrepo-brain__wire orgrepo-brain__wire--live' : undefined}
              style={flowing ? { animationDelay: `${(i % 5) * 0.4}s` } : undefined}/>
          );
        })}

        {/* nodes — every node shows its child count; click expands (synced to the
            table) and zooms in. Collapsed elements gently float. */}
        {layout.nodes.map((g, i) => {
          const node = g.node;
          const isEl = g.depth === 1;
          const open = expanded && expanded.has(node.path);
          const nodeR = isEl ? 7 : g.depth === 2 ? 5 : g.depth === 3 ? 4 : 3;
          const col = STATUS_COLOR[node.status] || '#9C8C82';
          const cos = Math.cos(g.angle);
          const anchor = cos > 0.34 ? 'start' : cos < -0.34 ? 'end' : 'middle';
          const loff = nodeR + 7;
          const lx = g.x + Math.cos(g.angle) * loff, ly = g.y + Math.sin(g.angle) * loff;
          const shortName = isEl ? node.label : node.name.replace(/\.md$/, '');
          const labelText = `${shortName}${g.hasKids ? (isEl ? ` · ${g.childCount}` : ` (${g.childCount})`) : ''}`;
          const floats = isEl && !open;
          return (
            <g key={`n${i}`} className={floats ? 'orgrepo-brain__node' : undefined}
              style={{ animationDelay: floats ? `${i * 0.5}s` : undefined, cursor: g.hasKids ? 'pointer' : 'default' }}
              onClick={() => g.hasKids && onToggleNode && onToggleNode(node.path)}>
              <circle cx={g.x} cy={g.y} r={nodeR} fill={col} stroke="#FCF8F7" strokeWidth={isEl ? 2 : 1.3}/>
              {isEl && <circle cx={g.x} cy={g.y} r={nodeR} fill="none" stroke={col} strokeOpacity={0.22} strokeWidth={5}/>}
              {g.depth <= 3 && (
                <text x={lx} y={ly} textAnchor={anchor} dominantBaseline="middle"
                  fontSize={isEl ? 11.5 : g.depth === 2 ? 9 : 7.5} fontWeight={isEl ? 600 : 500}
                  fill="var(--fg)" style={{ pointerEvents: 'none' }}>{labelText}</text>
              )}
              <title>{`${isEl ? node.label : node.name} — ${STATUS_LABEL[node.status]}${g.hasKids ? `\n${g.childCount} children · click to ${open ? 'collapse' : 'expand'}` : ''}`}</title>
            </g>
          );
        })}

        {/* centre: eOS brain — click resets zoom */}
        <g className="orgrepo-brain__core" style={{ cursor: 'pointer' }} onClick={() => { setSmooth(true); applyView({ k: 1, tx: 0, ty: 0 }); }}>
          <circle cx={cx} cy={cy} r={13} fill="#FF6428"/>
          <circle cx={cx} cy={cy} r={13} fill="none" stroke="#FF6428" strokeOpacity={0.3} strokeWidth={10} className="orgrepo-brain__pulse"/>
          <text x={cx} y={cy + 30} textAnchor="middle" fontSize={13} fontWeight={700} fill="var(--fg)"
            style={{ fontFamily: 'var(--font-display, inherit)' }}>eOS</text>
          {data.root && (
            <text x={cx} y={cy + 45} textAnchor="middle" fontSize={9.5} fill="var(--fg-muted)">
              {data.root.metrics.mdFiles} md files · {fmtInt(data.root.metrics.ownModules + data.root.metrics.delegateModules)} modules
            </text>
          )}
        </g>
        </g>
      </svg>
      {view.k > 1 && (
        <button type="button" className="orgrepo-brain__reset"
          onClick={() => { setSmooth(true); applyView({ k: 1, tx: 0, ty: 0 }); }} title="Reset zoom">
          {view.k.toFixed(1)}× · reset
        </button>
      )}
      <div className="orgrepo-brain__legend">
        <span><StatusDot status="green" size={8}/> Healthy</span>
        <span><StatusDot status="orange" size={8}/> Watch</span>
        <span><StatusDot status="red" size={8}/> Error</span>
        <span style={{ opacity: 0.85 }}>· thicker = busier (24h) · flowing = active (30 min)</span>
        <span style={{ marginLeft: 'auto', color: STATUS_COLOR[rootStatus], fontWeight: 600 }}>
          Firm-wide: {STATUS_LABEL[rootStatus]}
        </span>
      </div>
    </div>
  );
};

/* ── OrgRepoTable ─────────────────────────────────────────────────────
 * The whole OrgRepo as one expandable directory table. */
const OrgRepoTable = ({ onOpenFile, activeId, onSelectElement }) => {
  const [data, setData] = React.useState(null);
  const [error, setError] = React.useState(null);
  const [expanded, setExpanded] = React.useState(() => new Set());
  // Last toggled node — shared with the brain so a table expand also zooms the
  // graphic (and vice-versa). The counter makes re-toggling the same path fire.
  const [focus, setFocus] = React.useState(null);

  React.useEffect(() => {
    let cancelled = false;
    getOrgRepoMetrics()
      .then(d => { if (!cancelled) { if (d.error && !d.elements?.length) setError(d.error); else setData(d); } })
      .catch(e => { if (!cancelled) setError(e.message); });
    return () => { cancelled = true; };
  }, []);

  // The one toggle both surfaces share: flips expansion + points the focus at
  // the node so the brain centres/zooms on it.
  const toggleNode = React.useCallback((path) => {
    setExpanded(prev => {
      const next = new Set(prev);
      next.has(path) ? next.delete(path) : next.add(path);
      return next;
    });
    setFocus({ path, n: Date.now() });
  }, []);
  const expandAll = () => { if (data) setExpanded(collectExpandablePaths(data.elements, new Set())); };
  const collapseAll = () => { setExpanded(new Set()); setFocus({ path: '__reset__', n: Date.now() }); };

  if (error) return <ErrorBanner>{error}</ErrorBanner>;
  if (!data) return <div style={{ padding: 20, textAlign: 'center', color: 'var(--fg-muted)', fontSize: 12 }}>Computing OrgRepo metrics…</div>;

  const visible = [];
  flattenMetricTree(data.elements, expanded, 0, visible);
  const root = data.root || { metrics: {}, status: 'green', counts: {} };

  return (
    <div>
      <OrgRepoBrain data={data} expanded={expanded} onToggleNode={toggleNode} focus={focus}/>

      <div style={{ display: 'flex', alignItems: 'center', gap: 10, margin: '6px 0 10px' }}>
        <span style={{ fontSize: 11, letterSpacing: '0.08em', textTransform: 'uppercase', color: 'var(--fg-muted)', fontWeight: 600 }}>
          Directory
        </span>
        <button type="button" className="btn-ghost" style={{ fontSize: 11.5, padding: '3px 9px' }} onClick={expandAll}>Expand all</button>
        <button type="button" className="btn-ghost" style={{ fontSize: 11.5, padding: '3px 9px' }} onClick={collapseAll}>Collapse all</button>
        <span style={{ marginLeft: 'auto', fontSize: 11.5, color: 'var(--fg-muted)' }}>
          Click a row to expand · click a file to open it
        </span>
      </div>

      <div style={{ overflowX: 'auto' }}>
        <table className="tbl orgrepo-metric-tbl">
          <thead>
            <tr>
              <th style={{ minWidth: 240 }}>Element / folder / file</th>
              <th style={{ width: 130 }}>Status</th>
              <th style={{ ...NUM_CELL, width: 78 }}>MD files</th>
              <th style={{ ...NUM_CELL, width: 70 }}>Tables</th>
              <th style={{ ...NUM_CELL, width: 96 }} title="Data rows across the tables in these files">Table elements</th>
              <th style={{ ...NUM_CELL, width: 130 }} title="Own code — the code that defines this element: its own slice of the in-code catalog (skills/sources/artifacts/agents), or the module a /sources/code doc documents. Lines · size.">Own code</th>
              <th style={{ ...NUM_CELL, width: 130 }} title="Delegates — the modules this element calls into (e.g. a native skill's dynamic imports). Count of distinct modules · their combined size.">Delegates</th>
              <th style={{ ...NUM_CELL, width: 130 }} title="Files / storage — the real produced files of an artifact type (count · size), and the D1 database + R2 bucket totals on the Sources rows.">Files / storage</th>
            </tr>
          </thead>
          <tbody>
            {visible.map(({ node, depth }) => {
              const hasKids = node.children && node.children.length;
              const isOpen = expanded.has(node.path);
              const m = node.metrics;
              const isFile = node.kind === 'file';
              const onRowClick = () => {
                if (hasKids) toggleNode(node.path);
                else if (isFile && onOpenFile) onOpenFile(node.path);
              };
              return (
                <tr key={node.path} className="orgrepo-metric-row" style={{ cursor: 'pointer' }} onClick={onRowClick}>
                  <td>
                    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6, paddingLeft: depth * 18 }}>
                      <span style={{ width: 14, display: 'inline-flex', justifyContent: 'center', color: 'var(--fg-muted)' }}>
                        {hasKids ? <Icon name={isOpen ? 'collapse' : 'expand'} size={11}/> : null}
                      </span>
                      <Icon name={KIND_ICON[node.kind] || 'file'} size={12}/>
                      <span style={{
                        fontSize: node.kind === 'element' ? 13.5 : 12.5,
                        fontWeight: node.kind === 'element' ? 700 : isFile ? 400 : 600,
                        color: node.kind === 'element' && node.color ? node.color : 'var(--fg)',
                      }}>
                        {node.kind === 'element' ? node.label : node.name}
                      </span>
                      {isFile && onOpenFile && (
                        <span style={{ fontSize: 10.5, color: 'var(--orange)', opacity: 0.0 }} className="orgrepo-open-hint">open →</span>
                      )}
                    </span>
                  </td>
                  <td><StatusBadge node={node}/></td>
                  <td style={NUM_CELL}>{fmtInt(m.mdFiles)}</td>
                  <td style={NUM_CELL}>{fmtInt(m.tables)}</td>
                  <td style={NUM_CELL}>{fmtInt(m.tableRows)}</td>
                  <td style={NUM_CELL}>{ownCodeCell(m)}</td>
                  <td style={NUM_CELL}>{delegateCell(m)}</td>
                  <td style={NUM_CELL}>{filesCell(node)}</td>
                </tr>
              );
            })}
          </tbody>
          <tfoot>
            <tr style={{ fontWeight: 700 }}>
              <td><span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}><Icon name="db" size={12}/> OrgRepo (all elements)</span></td>
              <td><StatusBadge node={root}/></td>
              <td style={NUM_CELL}>{fmtInt(root.metrics.mdFiles)}</td>
              <td style={NUM_CELL}>{fmtInt(root.metrics.tables)}</td>
              <td style={NUM_CELL}>{fmtInt(root.metrics.tableRows)}</td>
              <td style={NUM_CELL}>{ownCodeCell(root.metrics)}</td>
              <td style={NUM_CELL}>{delegateCell(root.metrics)}</td>
              <td style={NUM_CELL}>{root.metrics.fileCount > 0 ? <span>{fmtInt(root.metrics.fileCount)} files · {fmtSize(root.metrics.fileKb)}</span> : '—'}</td>
            </tr>
          </tfoot>
        </table>
      </div>
    </div>
  );
};

/* ── OrgRepoDependencies ────────────────────────────────────────────
 * The "Dependencies" tab. Embeds the self-contained dependency-analysis
 * artifacts generated by `generate-graph.js` (served from /public):
 *   • /topology.html          — high-level interactive service map
 *   • /topology-detailed.html — force-directed graph of every source file
 * Both are 100% self-contained (no CDN, nothing leaves the page), so we
 * just iframe them. DEPENDENCIES.md (the written map) lives in the repo. */
const OrgRepoDependencies = () => {
  const [sub, setSub] = React.useState('overview');   // 'overview' | 'detailed' | 'detailed-v2'
  const SRC = { overview: '/topology.html', detailed: '/topology-detailed.html', 'detailed-v2': '/topology-detailed-v2.html' };
  const src = SRC[sub] || SRC.overview;
  const subs = [
    { id: 'overview', label: 'Service map', icon: 'map' },
    { id: 'detailed', label: 'File graph', icon: 'git' },
    { id: 'detailed-v2', label: 'File graph v2', icon: 'compass' },
  ];
  return (
    <div style={{display:'flex',flexDirection:'column',gap:12}}>
      <div style={{display:'flex',alignItems:'center',gap:8,flexWrap:'wrap'}}>
        <div style={{display:'flex',gap:6}}>
          {subs.map(s => (
            <button key={s.id} type="button" onClick={() => setSub(s.id)} style={{
              display:'flex',alignItems:'center',gap:6,padding:'5px 12px',borderRadius:8,fontSize:12,fontWeight:500,cursor:'pointer',
              background: sub === s.id ? 'var(--fg)' : 'var(--bg-alt)',
              color: sub === s.id ? '#fff' : 'var(--fg)',
              border: '1px solid ' + (sub === s.id ? 'var(--fg)' : 'var(--border)')}}>
              <Icon name={s.icon} size={12}/><span>{s.label}</span>
            </button>
          ))}
        </div>
        <span style={{flex:1}}/>
        <a href={src} target="_blank" rel="noreferrer" style={{display:'inline-flex',alignItems:'center',gap:5,fontSize:12,color:'var(--fg-muted)',textDecoration:'none'}}>
          <Icon name="external-link" size={12}/> Open full screen
        </a>
      </div>
      <div style={{fontSize:12.5,color:'var(--fg-muted)',lineHeight:1.5}}>
        {sub === 'overview'
          ? 'High-level service topology — Web App · API Routes · Server Lib · Runner Worker · Build Scripts, plus the D1/R2 stores and external services. Hover a box to trace its links, click for details.'
          : sub === 'detailed-v2'
          ? 'Dependency Explorer (v2) — every source file as a node with a searchable sidebar, per-module toggles, and a node/edge inspector. Light theme. Auto-generated by generate-graph.js.'
          : 'Every source file as a node, import edges between them. Search to highlight, toggle modules in the sidebar, click a node for its imports / imported-by. Auto-generated by generate-graph.js.'}
      </div>
      <div style={{
        border:'1px solid var(--border)',borderRadius:12,overflow:'hidden',background:'#fff',
        height:'calc(100vh - 320px)',minHeight:520}}>
        <iframe
          key={sub}
          src={src}
          title={sub === 'overview' ? 'Service topology' : 'Detailed dependency graph'}
          style={{width:'100%',height:'100%',border:'none',display:'block'}}
        />
      </div>
    </div>
  );
};

export const OrgRepoView = () => {
  const [tree, setTree] = React.useState([]);
  const [repo, setRepo] = React.useState(null);
  const [mode, setMode] = React.useState('table');   // 'table' (default) | 'browse'
  const [activeElement, setActiveElement] = React.useState('intents');
  const [active, setActive] = React.useState(null);
  const [treeError, setTreeError] = React.useState(null);
  const [loading, setLoading] = React.useState(true);
  const [newFileFolder, setNewFileFolder] = React.useState(null);
  const [status, setStatus] = React.useState(null);

  const loadStatus = React.useCallback(() => {
    fetch('/api/orgrepo/status', { credentials: 'include' })
      .then(r => r.ok ? r.json() : null)
      .then(s => { if (s) setStatus(s); })
      .catch(() => { /* badges are best-effort */ });
  }, []);

  const reload = React.useCallback(() => {
    setLoading(true);
    setTreeError(null);
    loadStatus();
    getOrgRepoTree()
      .then(({ tree, repo }) => {
        setTree(tree || []);
        setRepo(repo);
        // Honour deep-links from elsewhere (e.g. the function editor's
        // "View MD"). The hint sets the active element + file in one go.
        let deepLink = null;
        try { deepLink = sessionStorage.getItem('erasphere.orgrepoPath'); } catch {}
        if (deepLink) {
          const fromDeep = (tree || []).find(f => f.path === deepLink);
          if (fromDeep) {
            const top = '/' + fromDeep.path.split('/')[1];
            const elementId = top.slice(1);
            if (ELEMENT_DEFS.some(e => e.id === elementId)) {
              setActiveElement(elementId);
            }
            setActive(fromDeep.path);
            setMode('browse');   // a deep-link wants the file browser, not the table
            try { sessionStorage.removeItem('erasphere.orgrepoPath'); } catch {}
            return;
          }
        }
      })
      .catch(err => setTreeError(err.message))
      .finally(() => setLoading(false));
  }, []);

  React.useEffect(() => { reload(); }, [reload]);

  // Files inside the active element's folder. index.md is always first, then
  // the _TEMPLATE.md (the master spec for this element type), then the rest.
  const filesInActive = React.useMemo(
    () => {
      const all = tree.filter(f => elementPrefixes(activeElement).some(pre => f.path.startsWith(pre)));
      // rank: index.md = 0, any _TEMPLATE.md = 1, everything else = 2
      const rank = (p) => {
        const leaf = p.split('/').pop();
        if (p === `/${activeElement}/index.md`) return 0;
        if (leaf === '_TEMPLATE.md') return 1;
        return 2;
      };
      return all.sort((a, b) => {
        const ra = rank(a.path), rb = rank(b.path);
        if (ra !== rb) return ra - rb;
        return a.path.localeCompare(b.path);
      });
    },
    [tree, activeElement],
  );

  // When switching element tabs, clear the active file (or pick the
  // first one if there is one — index.md after the pin above).
  React.useEffect(() => {
    if (active && elementPrefixes(activeElement).some(pre => active.startsWith(pre))) return;
    setActive(filesInActive[0]?.path || null);
  }, [activeElement, filesInActive, active]);

  const def = ELEMENT_DEFS.find(e => e.id === activeElement);

  // Size the two-pane rail to fill the viewport below the header/tabs so each
  // pane scrolls inside itself and the page itself never scrolls. Measure the
  // rail's offset from the top and expose it as --respo-top for the CSS calc.
  const railRef = React.useRef(null);
  React.useLayoutEffect(() => {
    const el = railRef.current;
    if (!el) return undefined;
    const setTop = () => {
      const top = el.getBoundingClientRect().top + window.scrollY;
      el.style.setProperty('--respo-top', `${Math.round(top) + 16}px`);
    };
    setTop();
    window.addEventListener('resize', setTop);
    return () => window.removeEventListener('resize', setTop);
  }, [loading, treeError, activeElement]);

  return (
    <div className="page">
      <div className="page__head">
        <div style={{display:'flex',alignItems:'center',gap:12,flexWrap:'wrap'}}>
          <h1 style={{margin:0}}>OrgRepo</h1>
          {repo && (
            <span style={{display:'inline-flex',alignItems:'center',gap:6,padding:'4px 10px',background:'var(--bg-card)',borderRadius:6,fontSize:11,fontFamily:'JetBrains Mono',color:'var(--fg-muted)'}}>
              <Icon name="db" size={11}/>
              <span>{repo.backend}: {repo.collection}</span>
              <span style={{opacity:0.7}}>· {tree.length} files</span>
            </span>
          )}
          {status && status.elements && status.elements.length > 0 && (() => {
            const synced = status.elements.filter(e => e.inSync).length;
            const total = status.elements.length;
            const ok = status.allInSync;
            const tip = [
              'Markdown is the single source of truth. md-led elements (intents · sources · rules · triggers · artifacts) are always in sync — the md IS the truth. DB-led elements (agents · functions · skills) should have a backing row per md.',
              '',
              ...status.elements.map(e => `${e.element}: ${e.mdCount} md, ${e.inSync ? '✓ in sync' : '✗ ' + e.dbCount + ' backing rows'} (${e.leading})`),
            ].join('\n');
            return (
              <span
                title={tip}
                style={{display:'inline-flex',alignItems:'center',gap:6,padding:'4px 10px',borderRadius:6,fontSize:11,fontFamily:'JetBrains Mono',
                  background: ok ? 'rgba(30,122,69,0.12)' : 'rgba(162,2,53,0.10)',
                  color: ok ? '#1E7A45' : '#A20235',
                  border: '1px solid ' + (ok ? 'rgba(30,122,69,0.35)' : 'rgba(162,2,53,0.35)')}}>
                <span style={{width:7,height:7,borderRadius:'50%',background: ok ? '#1E7A45' : '#A20235'}}/>
                <span>{ok ? 'md is source of truth · all elements consistent' : `${synced}/${total} elements consistent`}</span>
              </span>
            );
          })()}
        </div>
        <div className="lede">The firm-as-code. Every element of how the firm operates lives here as markdown — versioned, auditable, editable from this UI. Eight elements; <strong>Agents</strong> sit above them and orchestrate (managed in the Agents view — System / Public / Personal tabs).</div>
      </div>

      {/* View toggle: Table (the default landing) · Browse (file editor) · Dependencies. */}
      <div style={{display:'flex',gap:6,marginBottom:16}}>
        {[{id:'table',label:'Table',icon:'db'},{id:'browse',label:'Browse',icon:'file'},{id:'deps',label:'Dependencies',icon:'git'}].map(v => (
          <button key={v.id} type="button" onClick={() => setMode(v.id)} style={{
            display:'flex',alignItems:'center',gap:6,padding:'6px 14px',borderRadius:8,fontSize:12.5,fontWeight:500,cursor:'pointer',
            background: mode === v.id ? 'var(--orange)' : 'var(--bg-alt)',
            color: mode === v.id ? '#fff' : 'var(--fg)',
            border: '1px solid ' + (mode === v.id ? 'var(--orange)' : 'var(--border)')}}>
            <Icon name={v.icon} size={12}/><span>{v.label}</span>
          </button>
        ))}
      </div>

      {mode === 'table' && (
        <OrgRepoTable
          activeId={activeElement}
          onSelectElement={(id) => { setActiveElement(id); setMode('browse'); }}
          onOpenFile={(path) => {
            const elementId = path.split('/')[1];
            if (ELEMENT_DEFS.some(e => e.id === elementId)) setActiveElement(elementId);
            setActive(path);
            setMode('browse');
          }}
        />
      )}

      {mode === 'deps' && <OrgRepoDependencies />}

      {mode === 'browse' && (<>
      {/* Top: 8 element tabs. */}
      <div style={{display:'flex',gap:6,flexWrap:'wrap',marginBottom:18}}>
        {ELEMENT_DEFS.map(el => {
          const count = tree.filter(f => elementPrefixes(el.id).some(pre => f.path.startsWith(pre))).length;
          const isActive = activeElement === el.id;
          return (
            <button
              key={el.id}
              type="button"
              onClick={() => setActiveElement(el.id)}
              style={{
                display:'flex',alignItems:'center',gap:6,
                padding:'7px 12px',borderRadius:8,fontSize:12.5,fontWeight:500,
                cursor:'pointer',
                background: isActive ? el.color : 'var(--bg-alt)',
                color: isActive ? '#fff' : 'var(--fg)',
                border: '1px solid ' + (isActive ? el.color : 'var(--border)'),
                borderLeft: '3px solid ' + el.color,
              }}
            >
              <Icon name={el.icon} size={12}/>
              <span>{el.label}</span>
              <span style={{fontSize:11,opacity:0.75,marginLeft:4}}>{count}</span>
            </button>
          );
        })}
      </div>

      {treeError && <ErrorBanner>{treeError}</ErrorBanner>}
      {loading && <div style={{padding:20,textAlign:'center',color:'var(--fg-muted)',fontSize:12}}>Loading tree…</div>}

      {!loading && !treeError && (
        <div className="respo-rail respo-rail--fill" ref={railRef}>
          <div>
            <div className="respo-rail__list-head" style={{display:'flex',alignItems:'center',gap:6,marginBottom:8}}>
              <span style={{fontSize:11,letterSpacing:'0.08em',textTransform:'uppercase',color:'var(--fg-muted)',fontWeight:600}}>
                {def?.label ?? activeElement} · {filesInActive.length}
              </span>
              <button
                type="button"
                onClick={() => setNewFileFolder('/' + activeElement)}
                title={`New file in /${activeElement}`}
                style={{marginLeft:'auto',padding:'0 8px',background:'transparent',border:0,cursor:'pointer',color:'var(--fg-muted)',fontSize:16,lineHeight:1,borderRadius:4}}
              >＋</button>
            </div>
            <div className="repo-tree">
              {filesInActive.length === 0 && (
                <div style={{padding:'10px 12px',fontSize:12,color:'var(--fg-muted)',fontStyle:'italic',background:'var(--bg-alt)',borderRadius:8}}>
                  empty — click ＋ to add
                </div>
              )}
              {groupBySubcategory(activeElement, filesInActive).map((group, gi) => (
                <div key={group.label ?? `__idx${gi}`}>
                  {group.label && (
                    <div style={{fontSize:10,letterSpacing:'0.07em',textTransform:'uppercase',color:'var(--fg-muted)',fontWeight:700,margin:'10px 0 3px',paddingLeft:4}}>
                      {group.label} · {group.files.length}
                    </div>
                  )}
                  {group.files.map(f => {
                    const synced = status && status.files ? status.files[f.path] : undefined;
                    return (
                    <div key={f.path} className={`row ${active === f.path ? 'on' : ''}`} onClick={() => setActive(f.path)}>
                      <Icon name="file" size={11}/>
                      <span style={{fontSize:12.5}}>{f.path.split('/').pop()}</span>
                      {synced !== undefined && (
                        <span title={synced ? 'In sync with the database' : 'No matching database row — not in sync'}
                          style={{marginLeft:'auto',flexShrink:0,width:7,height:7,borderRadius:'50%',background: synced ? '#1E7A45' : '#A20235'}}/>
                      )}
                    </div>
                    );
                  })}
                </div>
              ))}
            </div>
          </div>

          {active
            ? <RepoFile path={active} synced={status && status.files ? status.files[active] : undefined} onCommitted={() => { reload(); }} onDeleted={() => { setActive(null); reload(); }}/>
            : <div style={{padding:24,color:'var(--fg-muted)',fontSize:13}}>Pick a file on the left, or click ＋ to add one.</div>}
        </div>
      )}
      </>)}

      {newFileFolder && (
        <NewFileModal
          folder={newFileFolder}
          existing={tree.map(f => f.path)}
          onClose={() => setNewFileFolder(null)}
          onCreated={path => {
            setNewFileFolder(null);
            setActive(path);
            reload();
          }}
        />
      )}
    </div>
  );
};

function titleToSlug(title) {
  return title
    .toLowerCase()
    .normalize('NFKD').replace(/[̀-ͯ]/g, '')   // strip diacritics
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 80);
}

function uniqueSlug(base, taken) {
  if (!taken.has(base)) return base;
  let n = 1;
  while (taken.has(`${base}-${n}`)) n++;
  return `${base}-${n}`;
}

const NewFileModal = ({ folder, existing, onClose, onCreated }) => {
  const [title, setTitle] = React.useState('');
  const [busy, setBusy] = React.useState(false);
  const [error, setError] = React.useState(null);

  // Build the set of taken slugs inside this folder (without .md) so
  // the auto-suffix has a precise universe to dodge.
  const taken = React.useMemo(() => {
    const out = new Set();
    const prefix = folder + '/';
    for (const p of existing) {
      if (p.startsWith(prefix) && p.endsWith('.md')) {
        out.add(p.slice(prefix.length, -3));  // strip prefix + '.md'
      }
    }
    return out;
  }, [existing, folder]);

  const baseSlug = titleToSlug(title.trim());
  const finalSlug = baseSlug ? uniqueSlug(baseSlug, taken) : '';
  const path = finalSlug ? `${folder}/${finalSlug}.md` : '';
  const wasSuffixed = baseSlug && finalSlug !== baseSlug;

  const submit = async e => {
    e.preventDefault();
    if (!finalSlug || busy) return;
    setBusy(true); setError(null);
    try {
      // Registry-backed elements start with the front-matter the md→DB
      // write-back keys off, so creating the file also creates its DB row.
      let content;
      if (folder === '/intents') {
        content = `---\nkind: task\nlifecycle: standing\nskills: []\nwhy: ""\n---\n\n# Intent: ${finalSlug} — ${title.trim()}\n\n*New intent. Describe its goal, then Input / Synthesize / Output.*\n`;
      } else if (folder === '/sources') {
        content = `---\nkind: own-data\nlifecycle: standing\nused_by_skills: []\n---\n\n# ${title.trim()}\n\n*New source. Describe what it is and which skills draw on it.*\n`;
      } else if (folder === '/trigger') {
        content = `---\ntype: event\nintents: []\n---\n\n# ${title.trim()}\n\n*New trigger. Describe how it fires and which intents it raises.*\n`;
      } else {
        content = `# ${title.trim()}\n\n*New file. Replace this with the real content.*\n`;
      }
      await putOrgRepoFile({ path, content, message: `create ${path}` });
      onCreated(path);
    } catch (err) {
      setError(err.message);
      setBusy(false);
    }
  };

  return (
    <div className="modal-mask" onClick={onClose}>
      <div className="modal" onClick={e => e.stopPropagation()} style={{position:'relative',maxWidth:520}}>
        <button className="modal__close" onClick={onClose}>×</button>
        <div style={{fontSize:11,letterSpacing:'0.08em',textTransform:'uppercase',color:'var(--orange)',marginBottom:6,fontWeight:600}}>New file in {folder}</div>
        <h3 style={{marginBottom:8}}>Add a markdown file</h3>
        <p style={{fontSize:13,color:'var(--fg-muted)',marginBottom:18,lineHeight:1.5}}>
          Just type a title. The filename slug is derived automatically and uniqued with a numeric suffix if needed.
        </p>

        <form onSubmit={submit}>
          <div className="field">
            <label>Title (becomes the H1)</label>
            <input
              className="input"
              placeholder="e.g. North Star 2027"
              value={title}
              onChange={e => setTitle(e.target.value)}
              autoFocus
            />
            {path && (
              <div style={{fontSize:11.5,color:'var(--fg-muted)',marginTop:6,fontFamily:'JetBrains Mono'}}>
                {path}
                {wasSuffixed && (
                  <span style={{marginLeft:8,color:'var(--orange)',fontFamily:'inherit',fontStyle:'italic'}}>
                    (auto-suffixed — "{baseSlug}" was taken)
                  </span>
                )}
              </div>
            )}
            {title.trim() && !baseSlug && (
              <div style={{fontSize:11.5,color:'var(--support-red)',marginTop:6}}>
                Title is empty after stripping non-alphanumeric characters. Try a regular phrase.
              </div>
            )}
          </div>

          {error && <div style={{padding:10,marginBottom:14,background:'#FCE6E0',color:'#A20235',borderRadius:8,fontSize:13}}>{error}</div>}

          <div style={{display:'flex',gap:8,justifyContent:'flex-end'}}>
            <button type="button" className="btn-ghost" onClick={onClose}>Cancel</button>
            <button type="submit" className="btn-orange" disabled={!finalSlug || busy}>
              {busy ? 'Creating…' : 'Create file'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

const RepoFile = ({ path, synced, onCommitted, onDeleted }) => {
  const [file, setFile] = React.useState(null);
  const [draft, setDraft] = React.useState('');
  const [editing, setEditing] = React.useState(false);
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState(null);
  const [committing, setCommitting] = React.useState(false);
  const [deleting, setDeleting] = React.useState(false);
  const [commitMessage, setCommitMessage] = React.useState('');
  const [lastCommit, setLastCommit] = React.useState(null);
  const [cascadeOpen, setCascadeOpen] = React.useState(false);

  // Cascade analysis is available on the firm's strategic guidance files.
  // Old /strategy/ paths still flag for back-compat through the API.
  const isStrategy = path.startsWith('/rules/guidelines/') || path.startsWith('/rules/standards/') || path.startsWith('/guidelines/') || path.startsWith('/strategy/');
  const isProtected = path === '/intents/index.md' || path === '/audit/index.md' || path === '/logs/index.md';

  React.useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    setLastCommit(null);
    setEditing(false);
    getOrgRepoFile(path)
      .then(f => {
        if (cancelled) return;
        setFile(f);
        setDraft(f.content);
      })
      .catch(err => { if (!cancelled) setError(err.message); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [path]);

  const dirty = file && draft !== file.content;

  const remove = async () => {
    if (!file || deleting || isProtected) return;
    if (!confirm(`Delete ${path}? Its history stays in orgrepo_revisions but the live file goes away.`)) return;
    setDeleting(true); setError(null);
    try {
      await deleteOrgRepoFile(path);
      onDeleted?.();
    } catch (err) {
      setError(err.message);
      setDeleting(false);
    }
  };

  const save = async () => {
    if (!dirty || committing) return;
    setCommitting(true);
    setError(null);
    try {
      const res = await putOrgRepoFile({
        path,
        content: draft,
        sha: file.sha,
        message: commitMessage.trim() || `update ${path}`,
      });
      setFile({ ...file, content: draft, sha: res.sha });
      setLastCommit(res);
      setCommitMessage('');
      setEditing(false);
      onCommitted?.();
    } catch (err) {
      setError(err.message);
    } finally {
      setCommitting(false);
    }
  };

  const cancel = () => {
    if (committing) return;
    if (dirty && !confirm('Discard your unsaved changes?')) return;
    setDraft(file?.content || '');
    setCommitMessage('');
    setEditing(false);
  };

  return (
    <div className="md-editor">
      <div className="md-editor__head">
        <Icon name="file" size={13}/>
        <span className="md-editor__path">{path}</span>
        {file && <span style={{color:'var(--fg-muted)',fontSize:11,fontFamily:'JetBrains Mono'}}>· sha {file.sha.slice(0,7)}</span>}
        <div style={{flex:1}}/>

        {!editing ? (
          // ── Read-mode toolbar ──────────────────────────────────
          <>
            {lastCommit && <span style={{fontSize:11,color:'var(--support-green)',fontFamily:'JetBrains Mono'}}>● Saved · rev {lastCommit.sha.slice(0,7)}{lastCommit.prUrl && <> · <a href={lastCommit.prUrl} target="_blank" rel="noopener noreferrer" style={{color:'var(--support-green)'}}>PR opened</a></>}</span>}
            {!isProtected && file && (
              <button
                className="btn-ghost"
                style={{padding:'5px 10px',fontSize:12,color:'var(--support-red)'}}
                onClick={remove}
                disabled={deleting}
                title="Delete this file"
              >
                {deleting ? 'Deleting…' : '🗑'}
              </button>
            )}
            {file && (
              <button
                className="btn-orange"
                style={{padding:'5px 14px',fontSize:13}}
                onClick={() => setEditing(true)}
                title="Switch to the markdown editor"
              >
                ✎ Edit
              </button>
            )}
          </>
        ) : (
          // ── Edit-mode toolbar ──────────────────────────────────
          <>
            {dirty && <span style={{fontSize:11,color:'var(--orange)'}}>● Modified</span>}
            <input
              className="input"
              style={{padding:'5px 10px',fontSize:12,maxWidth:240}}
              placeholder="Commit message — why is this revision different?"
              title="A one-liner describing why this revision is different from the last. Saved with the new version into orgrepo_revisions so the history pane can show what changed and why."
              value={commitMessage}
              onChange={e => setCommitMessage(e.target.value)}
              disabled={!dirty || committing}
            />
            {isStrategy && file && (
              <button
                className="btn-ghost"
                style={{padding:'5px 12px',fontSize:12}}
                onClick={() => setCascadeOpen(true)}
                disabled={!dirty || committing}
                title={dirty
                  ? 'Run cascade analysis on the unsaved diff and approve+commit in one step'
                  : 'Edit the file first to enable cascade analysis'}
              >
                ✨ Approve & commit cascade
              </button>
            )}
            <button
              className="btn-ghost"
              style={{padding:'5px 12px',fontSize:13}}
              onClick={cancel}
              disabled={committing}
            >
              Cancel
            </button>
            <button
              className="btn-orange"
              style={{padding:'5px 14px',fontSize:13}}
              onClick={save}
              disabled={!dirty || committing}
            >
              {committing ? 'Saving…' : <><Icon name="check" size={12}/> Save</>}
            </button>
          </>
        )}
      </div>

      {loading && <div style={{padding:60,textAlign:'center',color:'var(--fg-muted)'}}>Loading…</div>}
      {error && <div style={{padding:14}}><ErrorBanner>{error}</ErrorBanner></div>}

      {cascadeOpen && (
        <CascadeAnalyzeModal
          path={path}
          oldContent={file?.content || ''}
          newContent={draft}
          dirty={dirty}
          onClose={() => setCascadeOpen(false)}
          onApproveAndCommit={async () => {
            await save();
            setCascadeOpen(false);
          }}
        />
      )}

      {!loading && file && !editing && (
        <div
          className="md-editor__body"
          onDoubleClick={() => setEditing(true)}
          title="Double-click to edit"
        >
          <MarkdownView source={file.content}/>
        </div>
      )}

      {!loading && file && editing && (
        <textarea
          className="md-editor__body"
          style={{display:'block',width:'100%',border:0,resize:'vertical',fontFamily:'JetBrains Mono',fontSize:13,lineHeight:1.6,padding:'24px 32px',minHeight:480,outline:'none',background:'var(--bg)'}}
          value={draft}
          onChange={e => setDraft(e.target.value)}
          spellCheck={false}
          autoFocus
        />
      )}
    </div>
  );
};

/* ─── Minimal markdown renderer ─────────────────────────────────────
   Handles the subset of markdown OrgRepo files actually use:
   headings (# .. ###), paragraphs, line breaks, bold + italic,
   inline code, fenced code blocks, blockquotes, ordered + unordered
   lists, horizontal rules, links. No nested lists, no tables — keep
   the parser small. Falls back to raw text in <pre>-like display
   whenever something doesn't match a known block. */

/**
 * Renders a ```people-cards fenced block. Spec shape (produced by the
 * `people-cards` native skill in `functions/_lib/skills.ts`):
 *   {
 *     title?: string,
 *     users: [{
 *       id, name, title?, photoUrl?, organizations: string[],
 *       country?, reasoning: string  (≤250 chars)
 *     }]
 *   }
 * Click any card → navigates to that person's full profile page.
 */
export const PeopleCardsFence = ({ spec }) => {
  const users = Array.isArray(spec?.users) ? spec.users : [];
  if (users.length === 0) {
    return (
      <div style={{padding:12,background:'var(--bg-alt)',borderRadius:8,fontSize:12.5,color:'var(--fg-muted)',fontStyle:'italic'}}>
        people-cards: empty shortlist.
      </div>
    );
  }
  return (
    <div style={{margin:'12px 0'}}>
      {spec?.title && (
        <div style={{
          fontSize:11,letterSpacing:'0.08em',textTransform:'uppercase',
          color:'var(--fg-muted)',marginBottom:8,fontWeight:600,
        }}>
          {spec.title} · {users.length}
        </div>
      )}
      <div style={{
        display:'grid',
        gridTemplateColumns:'repeat(auto-fill, minmax(260px, 1fr))',
        gap:10,
      }}>
        {users.map(u => (
          <button
            key={u.id}
            type="button"
            onClick={() => { window.location.hash = 'people/' + u.id; }}
            style={{
              textAlign:'left',
              padding:12,background:'var(--bg-alt)',borderRadius:10,
              border:'1px solid var(--border)',
              cursor:'pointer',display:'flex',flexDirection:'column',gap:8,
              fontFamily:'inherit',color:'inherit',
            }}
          >
            <div style={{display:'flex',alignItems:'center',gap:10}}>
              <Avatar src={u.photoUrl} name={u.name} size="md"/>
              <div style={{minWidth:0,flex:1}}>
                <div style={{fontSize:13.5,fontWeight:600,lineHeight:1.2,overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap'}}>
                  {u.name}
                </div>
                {u.title && (
                  <div style={{fontSize:11.5,color:'var(--fg-muted)',marginTop:2,overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap'}}>
                    {u.title}
                  </div>
                )}
                {(u.organizations?.length > 0 || u.country) && (
                  <div style={{fontSize:11,color:'var(--fg-muted)',marginTop:1,overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap'}}>
                    {[u.organizations?.join(' · '), u.country].filter(Boolean).join(' · ')}
                  </div>
                )}
              </div>
            </div>
            {u.reasoning && (
              <div style={{
                fontSize:12,lineHeight:1.45,color:'var(--fg)',
                overflow:'hidden',textOverflow:'ellipsis',display:'-webkit-box',
                WebkitLineClamp:3,WebkitBoxOrient:'vertical',
              }}>
                {u.reasoning.length > 250 ? u.reasoning.slice(0, 250) + '…' : u.reasoning}
              </div>
            )}
          </button>
        ))}
      </div>
    </div>
  );
};

const PROJECT_STATUS_COLOR = {
  lead:         { bg: '#FFE7DC', fg: '#B83C0F', label: 'Lead' },
  'won-waiting':{ bg: '#FFF1D0', fg: '#8C5A00', label: 'Won · waiting' },
  running:      { bg: '#D7F3DB', fg: '#136127', label: 'Running' },
  finished:     { bg: '#E8E0FF', fg: '#3B2D8C', label: 'Finished' },
  lost:         { bg: '#FCE6E0', fg: '#A20235', label: 'Lost' },
};

function projectStatusPill(status) {
  const key = (status || '').toLowerCase();
  const meta = PROJECT_STATUS_COLOR[key] || { bg: 'var(--bg-alt)', fg: 'var(--fg-muted)', label: status || '—' };
  return (
    <span style={{
      display:'inline-block',padding:'1px 7px',fontSize:10,fontWeight:700,
      letterSpacing:'0.04em',textTransform:'uppercase',
      background:meta.bg,color:meta.fg,borderRadius:6,
    }}>{meta.label}</span>
  );
}

function fmtProjectDate(ms) {
  if (!ms) return null;
  try { return new Date(ms).toLocaleDateString(undefined, { month: 'short', year: 'numeric' }); }
  catch { return null; }
}

/**
 * Renders a ```project-cards fenced block. Spec shape (from
 * the `project-cards` skill):
 *   {
 *     title?: string,
 *     projects: [{ id, slug, name, clientName, status, startsAt,
 *                  endsAt, volume, teamSize, reasoning }]
 *   }
 * Click a card → navigates to that project's detail page.
 */
export const ProjectCardsFence = ({ spec }) => {
  const projects = Array.isArray(spec?.projects) ? spec.projects : [];
  if (projects.length === 0) {
    return (
      <div style={{padding:12,background:'var(--bg-alt)',borderRadius:8,fontSize:12.5,color:'var(--fg-muted)',fontStyle:'italic'}}>
        project-cards: empty shortlist.
      </div>
    );
  }
  return (
    <div style={{margin:'12px 0'}}>
      {spec?.title && (
        <div style={{fontSize:11,letterSpacing:'0.08em',textTransform:'uppercase',color:'var(--fg-muted)',marginBottom:8,fontWeight:600}}>
          {spec.title} · {projects.length}
        </div>
      )}
      <div style={{display:'grid',gridTemplateColumns:'repeat(auto-fill, minmax(280px, 1fr))',gap:10}}>
        {projects.map(p => {
          const start = fmtProjectDate(p.startsAt);
          const end = fmtProjectDate(p.endsAt);
          const dates = [start, end].filter(Boolean).join(' → ');
          return (
            <a
              key={p.id}
              href={`#projects/${p.slug || p.id}`}
              style={{
                textDecoration:'none',color:'inherit',
                padding:12,background:'var(--bg-alt)',borderRadius:10,
                border:'1px solid var(--border)',
                display:'flex',flexDirection:'column',gap:8,
              }}
            >
              <div style={{display:'flex',alignItems:'center',gap:8,flexWrap:'wrap'}}>
                {projectStatusPill(p.status)}
                {p.clientName && (
                  <span style={{fontSize:11.5,color:'var(--fg-muted)',fontStyle:'italic'}}>{p.clientName}</span>
                )}
              </div>
              <div style={{fontSize:13.5,fontWeight:600,lineHeight:1.25,wordBreak:'break-word'}}>
                {p.name}
              </div>
              <div style={{display:'flex',gap:10,fontSize:11,color:'var(--fg-muted)',flexWrap:'wrap'}}>
                {dates && <span>{dates}</span>}
                {p.volume && <span>{p.volume}</span>}
                {p.teamSize > 0 && <span>{p.teamSize} {p.teamSize === 1 ? 'person' : 'people'}</span>}
              </div>
              {p.reasoning && (
                <div style={{fontSize:12,lineHeight:1.45,color:'var(--fg)',overflow:'hidden',textOverflow:'ellipsis',display:'-webkit-box',WebkitLineClamp:3,WebkitBoxOrient:'vertical'}}>
                  {p.reasoning.length > 250 ? p.reasoning.slice(0, 250) + '…' : p.reasoning}
                </div>
              )}
            </a>
          );
        })}
      </div>
    </div>
  );
};

/**
 * Renders a ```client-cards fenced block. Spec shape:
 *   { title?: string, clients: [{ id, slug, name, industry, logoUrl,
 *     projectCount, teamSize, reasoning }] }
 * Click → navigates to that client's detail page.
 */
export const ClientCardsFence = ({ spec }) => {
  const clients = Array.isArray(spec?.clients) ? spec.clients : [];
  if (clients.length === 0) {
    return (
      <div style={{padding:12,background:'var(--bg-alt)',borderRadius:8,fontSize:12.5,color:'var(--fg-muted)',fontStyle:'italic'}}>
        client-cards: empty shortlist.
      </div>
    );
  }
  return (
    <div style={{margin:'12px 0'}}>
      {spec?.title && (
        <div style={{fontSize:11,letterSpacing:'0.08em',textTransform:'uppercase',color:'var(--fg-muted)',marginBottom:8,fontWeight:600}}>
          {spec.title} · {clients.length}
        </div>
      )}
      <div style={{display:'grid',gridTemplateColumns:'repeat(auto-fill, minmax(260px, 1fr))',gap:10}}>
        {clients.map(c => (
          <a
            key={c.id}
            href={`#clients/${c.slug || c.id}`}
            style={{
              textDecoration:'none',color:'inherit',
              padding:12,background:'var(--bg-alt)',borderRadius:10,
              border:'1px solid var(--border)',
              display:'flex',flexDirection:'column',gap:8,
            }}
          >
            <div style={{display:'flex',alignItems:'center',gap:10}}>
              {c.logoUrl
                ? <img src={c.logoUrl} alt={c.name} style={{width:36,height:36,borderRadius:6,objectFit:'contain',background:'var(--bg)',padding:2,flexShrink:0}}/>
                : <div style={{width:36,height:36,borderRadius:6,background:'var(--bg)',display:'flex',alignItems:'center',justifyContent:'center',fontSize:11,fontWeight:700,color:'var(--fg-muted)',flexShrink:0}}>{(c.name || '?').slice(0, 2).toUpperCase()}</div>}
              <div style={{minWidth:0,flex:1}}>
                <div style={{fontSize:13.5,fontWeight:600,lineHeight:1.2,overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap'}}>
                  {c.name}
                </div>
                {c.industry && (
                  <div style={{fontSize:11.5,color:'var(--fg-muted)',marginTop:2,overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap'}}>
                    {c.industry}
                  </div>
                )}
              </div>
            </div>
            <div style={{display:'flex',gap:10,fontSize:11,color:'var(--fg-muted)',flexWrap:'wrap'}}>
              {c.projectCount > 0 && <span>{c.projectCount} project{c.projectCount === 1 ? '' : 's'}</span>}
              {c.teamSize > 0 && <span>{c.teamSize} {c.teamSize === 1 ? 'person' : 'people'} on account</span>}
            </div>
            {c.reasoning && (
              <div style={{fontSize:12,lineHeight:1.45,color:'var(--fg)',overflow:'hidden',textOverflow:'ellipsis',display:'-webkit-box',WebkitLineClamp:3,WebkitBoxOrient:'vertical'}}>
                {c.reasoning.length > 250 ? c.reasoning.slice(0, 250) + '…' : c.reasoning}
              </div>
            )}
          </a>
        ))}
      </div>
    </div>
  );
};

export const MarkdownView = React.memo(function MarkdownView({ source, allowHtml = false }) {
  return <>{renderBlocks(source || '', allowHtml)}</>;
});

function renderBlocks(text, allowHtml = false) {
  const lines = text.replace(/\r\n/g, '\n').split('\n');
  const blocks = [];
  let i = 0;

  while (i < lines.length) {
    const line = lines[i];

    // Fenced code block: ``` … ```
    // Lang regex accepts hyphens so multi-word language tags like
    // "people-cards" parse correctly. Without this fix the `\w*`
    // version captured just "people" and our PeopleCardsFence
    // renderer never fired.
    if (/^```/.test(line)) {
      const fence = line.match(/^```([A-Za-z0-9_-]*)/);
      const lang = fence?.[1] || '';
      const buf = [];
      i++;
      while (i < lines.length && !/^```/.test(lines[i])) { buf.push(lines[i]); i++; }
      if (i < lines.length) i++; // skip closing fence
      blocks.push({ t: 'code', lang, text: buf.join('\n') });
      continue;
    }

    // HTML comment block — skip entirely (used as generated-section
    // markers, e.g. the auto-built D1 table list in the manual). Spans
    // multiple lines until the line containing the closing `-->`.
    if (/^\s*<!--/.test(line)) {
      while (i < lines.length && !/-->/.test(lines[i])) i++;
      if (i < lines.length) i++; // consume the line with -->
      continue;
    }

    // Horizontal rule
    if (/^\s*(-{3,}|\*{3,}|_{3,})\s*$/.test(line)) {
      blocks.push({ t: 'hr' });
      i++;
      continue;
    }

    // Headings #, ##, ###
    const h = line.match(/^(#{1,3})\s+(.*)$/);
    if (h) {
      blocks.push({ t: 'h', level: h[1].length, text: h[2].trim() });
      i++;
      continue;
    }

    // Blockquote (contiguous > lines)
    if (/^>\s?/.test(line)) {
      const buf = [];
      while (i < lines.length && /^>\s?/.test(lines[i])) {
        buf.push(lines[i].replace(/^>\s?/, ''));
        i++;
      }
      blocks.push({ t: 'quote', text: buf.join('\n') });
      continue;
    }

    // Lists (- + * or n.) with continuation-line support.
    //
    // A bullet item can wrap across multiple lines — the convention
    // is that the wrap line is indented (typically 2 spaces) so the
    // text aligns under the bullet's content. Previously this parser
    // would close the list at the first wrap line and emit the
    // continuation as a stray paragraph, producing the "list items
    // and prose interleaved" rendering bug in the onboarding view.
    if (/^\s*([-*+]|\d+\.)\s+/.test(line)) {
      const ordered = /^\s*\d+\.\s/.test(line);
      const items = [];
      while (i < lines.length) {
        const cur = lines[i];
        if (/^\s*([-*+]|\d+\.)\s+/.test(cur)) {
          items.push(cur.replace(/^\s*(?:[-*+]|\d+\.)\s+/, ''));
          i++;
          // Pull subsequent indented (or non-empty non-bullet) wrap
          // lines into the current item until we hit a blank line,
          // a new bullet, or a structural marker.
          while (i < lines.length) {
            const nxt = lines[i];
            if (nxt.trim() === '') break;
            if (/^\s*([-*+]|\d+\.)\s+/.test(nxt)) break;
            if (/^```|^#{1,3}\s+|^>\s?|^\s*(-{3,}|\*{3,}|_{3,})\s*$/.test(nxt)) break;
            // Continuation line — append with a space so the bullet
            // text reads as one paragraph.
            items[items.length - 1] += ' ' + nxt.trim();
            i++;
          }
        } else {
          break;
        }
      }
      blocks.push({ t: 'list', ordered, items });
      continue;
    }

    // Blank line — skip
    if (line.trim() === '') { i++; continue; }

    // GFM-style tables: header `| a | b |` followed by separator
    // `| --- | --- |` followed by ≥1 body row.
    if (isTableRow(line) && i + 1 < lines.length && isTableSeparator(lines[i + 1])) {
      const headerCells = splitTableCells(line);
      const sepCells = splitTableCells(lines[i + 1]);
      // Optional alignment per column from the separator (--- :--- ---: :---:).
      const aligns = sepCells.map(cell => {
        const c = cell.trim();
        const left = c.startsWith(':');
        const right = c.endsWith(':');
        return left && right ? 'center' : right ? 'right' : left ? 'left' : null;
      });
      i += 2;
      const rows = [];
      while (i < lines.length && isTableRow(lines[i])) {
        rows.push(splitTableCells(lines[i]));
        i++;
      }
      blocks.push({ t: 'table', headers: headerCells, aligns, rows });
      continue;
    }

    // Otherwise: a paragraph runs until a blank line / heading / list / fence / table.
    const buf = [line];
    i++;
    while (i < lines.length) {
      const l = lines[i];
      if (l.trim() === '') break;
      if (/^```|^#{1,3}\s+|^>\s?|^\s*([-*+]|\d+\.)\s+|^\s*(-{3,}|\*{3,}|_{3,})\s*$/.test(l)) break;
      // Don't swallow the first row of a fresh table into a paragraph.
      if (isTableRow(l) && i + 1 < lines.length && isTableSeparator(lines[i + 1])) break;
      buf.push(l);
      i++;
    }
    blocks.push({ t: 'p', text: buf.join('\n') });
  }

  return blocks.map((b, idx) => renderBlock(b, idx, allowHtml));
}

// A line counts as a table row if it has at least one unescaped `|` and
// either starts/ends with `|` or contains a `|` between non-space chars.
function isTableRow(line) {
  if (!line.includes('|')) return false;
  const trimmed = line.trim();
  return trimmed.startsWith('|') || trimmed.endsWith('|') || /\S\s*\|\s*\S/.test(trimmed);
}
function isTableSeparator(line) {
  if (!isTableRow(line)) return false;
  // Each cell must be made of `-`, `:`, and whitespace only, with at
  // least 3 dashes so a single-character `-` doesn't pass.
  const cells = splitTableCells(line);
  return cells.length > 0 && cells.every(c => /^:?-{3,}:?$/.test(c.trim()));
}
function splitTableCells(line) {
  let t = line.trim();
  if (t.startsWith('|')) t = t.slice(1);
  if (t.endsWith('|'))   t = t.slice(0, -1);
  // Split on un-escaped pipes (`\|` allowed inside a cell).
  const cells = [];
  let cur = '';
  for (let k = 0; k < t.length; k++) {
    if (t[k] === '\\' && t[k + 1] === '|') { cur += '|'; k++; continue; }
    if (t[k] === '|') { cells.push(cur); cur = ''; continue; }
    cur += t[k];
  }
  cells.push(cur);
  return cells.map(c => c.trim());
}

// If a ```staffing fence body fails JSON.parse (e.g. the model
// paraphrased it and mangled quotes), salvage the interactive embed by
// pulling the params out with regex rather than dumping raw JSON.
function recoverStaffingSpec(text) {
  if (!text) return null;
  const grab = re => { const m = text.match(re); return m ? m[1] : null; };
  const search = grab(/["“]?search["”]?\s*:\s*["“]+([^"“”]*)["”]+/i);
  const projectId = grab(/["“]?projectId["”]?\s*:\s*["“]+([^"“”]*)["”]+/i);
  const weeksRaw = grab(/["“]?weeks["”]?\s*:\s*"?(\d{1,2})"?/i);
  if (search === null && projectId === null && weeksRaw === null) return null;
  return { params: { search: search || '', projectId: projectId || '', weeks: weeksRaw ? Number(weeksRaw) : 12 } };
}

function renderBlock(b, key, allowHtml = false) {
  switch (b.t) {
    case 'hr':
      return <hr key={key}/>;
    case 'h':
      if (b.level === 1) return <h1 key={key}>{renderInline(b.text)}</h1>;
      if (b.level === 2) return <h2 key={key}>{renderInline(b.text)}</h2>;
      return <h3 key={key}>{renderInline(b.text)}</h3>;
    case 'code':
      // Fenced ```chart … ``` blocks render as a Recharts component
      // instead of as monospace text. The skill emits the JSON spec
      // inside the fence; we parse and mount it. Other languages
      // (no lang or unknown) stay as <pre><code>.
      if (b.lang === 'mermaid') {
        return <MermaidFence key={key} source={b.text}/>;
      }
      // ```html-fig … ``` — a verbatim HTML/SVG figure (the OrgRepo-logic
      // graphs recovered 1:1 from the old /org-logic page). Rendered raw so
      // the hand-tuned flowmap / taxonomy table / swimlane SVG appear exactly
      // as authored. Gated behind `allowHtml`: only the trusted in-repo Manual
      // (README.md) opts in — OrgRepo files, memos and chat artefacts never do,
      // so this is not a general HTML-injection surface. Scoped under .orgfig.
      if (b.lang === 'html-fig' && allowHtml) {
        return <figure key={key} className="orgfig" dangerouslySetInnerHTML={{ __html: b.text }}/>;
      }
      if (b.lang === 'chart') {
        let spec = null;
        try { spec = JSON.parse(b.text); }
        catch { /* fall through to code rendering below */ }
        if (spec) return <InteractiveChart key={key} spec={spec}/>;
      }
      if (b.lang === 'people-cards') {
        let spec = null;
        try { spec = JSON.parse(b.text); }
        catch { /* fall through to plain code */ }
        if (spec && Array.isArray(spec.users)) return <PeopleCardsFence key={key} spec={spec}/>;
      }
      if (b.lang === 'project-cards') {
        let spec = null;
        try { spec = JSON.parse(b.text); }
        catch { /* fall through to plain code */ }
        if (spec && Array.isArray(spec.projects)) return <ProjectCardsFence key={key} spec={spec}/>;
      }
      if (b.lang === 'client-cards') {
        let spec = null;
        try { spec = JSON.parse(b.text); }
        catch { /* fall through to plain code */ }
        if (spec && Array.isArray(spec.clients)) return <ClientCardsFence key={key} spec={spec}/>;
      }
      if (b.lang === 'staffing') {
        let spec = null;
        try { spec = JSON.parse(b.text); }
        catch { spec = recoverStaffingSpec(b.text); }
        if (spec && (spec.params || Array.isArray(spec.weeks))) return <StaffingFence key={key} spec={spec}/>;
      }
      if (b.lang === 'project-lifecycle') {
        let spec = null;
        try { spec = JSON.parse(b.text); }
        catch { /* fall through to plain code */ }
        if (spec && (spec.projectId || spec.projectSlug)) return <ProjectLifecycleFence key={key} spec={spec}/>;
      }
      return <pre key={key} data-lang={b.lang || undefined}><code>{b.text}</code></pre>;
    case 'quote':
      return <blockquote key={key}>{renderBlocks(b.text, allowHtml)}</blockquote>;
    case 'list':
      if (b.ordered) return <ol key={key}>{b.items.map((it, i) => <li key={i}>{renderInline(it)}</li>)}</ol>;
      return <ul key={key}>{b.items.map((it, i) => <li key={i}>{renderInline(it)}</li>)}</ul>;
    case 'table':
      return (
        <table key={key}>
          <thead>
            <tr>
              {b.headers.map((h, hi) => (
                <th key={hi} style={b.aligns[hi] ? { textAlign: b.aligns[hi] } : undefined}>
                  {renderInline(h)}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {b.rows.map((row, ri) => (
              <tr key={ri}>
                {b.headers.map((_, ci) => (
                  <td key={ci} style={b.aligns[ci] ? { textAlign: b.aligns[ci] } : undefined}>
                    {renderInline(row[ci] != null ? row[ci] : '')}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      );
    case 'p': {
      // CommonMark line-break semantics:
      //   - A bare newline inside a paragraph is a *soft* break that
      //     collapses to a single space (so source wrapped at ~75 cols
      //     reads as one flowing paragraph — what readers expect).
      //   - A *hard* break (rendered as <br/>) is only requested when
      //     the source line ends in two spaces or a backslash.
      // The previous implementation treated every \n as a hard break,
      // which made the live Manual show every README wrap as a forced
      // line break.
      const raw = b.text.split('\n');
      const segs = [];
      let cur = '';
      for (let li = 0; li < raw.length; li++) {
        let l = raw[li];
        const isLast = li === raw.length - 1;
        let hard = false;
        if (l.endsWith('\\')) { hard = true; l = l.slice(0, -1); }
        else if (/  +$/.test(l)) { hard = true; l = l.replace(/\s+$/, ''); }
        // Strip leading whitespace on continuation lines so the join
        // doesn't double-space when source lines are indented.
        const piece = li === 0 ? l : l.replace(/^\s+/, '');
        cur += (cur ? ' ' : '') + piece;
        if (hard || isLast) {
          segs.push({ text: cur, br: hard && !isLast });
          cur = '';
        }
      }
      return (
        <p key={key}>
          {segs.map((s, i) => (
            <React.Fragment key={i}>
              {renderInline(s.text)}
              {s.br && <br/>}
            </React.Fragment>
          ))}
        </p>
      );
    }
    default:
      return null;
  }
}

// Inline pass: bold, italic, inline code, links. Tokenises by walking
// the string and matching the next inline marker.
function renderInline(text) {
  const out = [];
  let i = 0;
  let buf = '';
  const flush = () => { if (buf) { out.push(buf); buf = ''; } };

  while (i < text.length) {
    // Inline code: `…`
    if (text[i] === '`') {
      const end = text.indexOf('`', i + 1);
      if (end > -1) {
        flush();
        out.push(<code key={out.length}>{text.slice(i + 1, end)}</code>);
        i = end + 1;
        continue;
      }
    }
    // Bold: **…**
    if (text[i] === '*' && text[i + 1] === '*') {
      const end = text.indexOf('**', i + 2);
      if (end > -1) {
        flush();
        out.push(<strong key={out.length}>{renderInline(text.slice(i + 2, end))}</strong>);
        i = end + 2;
        continue;
      }
    }
    // Italic: *…*  (single asterisk; avoid lone * by requiring non-space after)
    if (text[i] === '*' && text[i + 1] !== '*' && text[i + 1] !== ' ' && text[i + 1] !== undefined) {
      const end = text.indexOf('*', i + 1);
      if (end > -1 && text[end - 1] !== ' ') {
        flush();
        out.push(<em key={out.length}>{renderInline(text.slice(i + 1, end))}</em>);
        i = end + 1;
        continue;
      }
    }
    // Image: ![alt](url) — supports http(s), root-relative, and the
    // `data:image/...;base64,...` URIs the chart skills emit. Renders
    // as a max-width-100% block image so SVG charts scale cleanly in
    // the chat bubble.
    if (text[i] === '!' && text[i + 1] === '[') {
      const closeBr = text.indexOf(']', i + 2);
      if (closeBr > -1 && text[closeBr + 1] === '(') {
        const closePar = text.indexOf(')', closeBr + 2);
        if (closePar > -1) {
          const alt = text.slice(i + 2, closeBr);
          const src = text.slice(closeBr + 2, closePar);
          if (/^(https?:\/\/|\/|data:image\/)/.test(src)) {
            flush();
            out.push(
              <img
                key={out.length}
                src={src}
                alt={alt}
                style={{maxWidth:'100%',height:'auto',borderRadius:8,margin:'6px 0',display:'block'}}
              />,
            );
            i = closePar + 1;
            continue;
          }
        }
      }
    }
    // Link: [text](url)
    if (text[i] === '[') {
      const closeBr = text.indexOf(']', i + 1);
      if (closeBr > -1 && text[closeBr + 1] === '(') {
        const closePar = text.indexOf(')', closeBr + 2);
        if (closePar > -1) {
          const label = text.slice(i + 1, closeBr);
          const href = text.slice(closeBr + 2, closePar);
          // Allow http(s), root-relative, and anchors; otherwise skip the link interpretation.
          if (/^(https?:\/\/|\/|#)/.test(href)) {
            flush();
            out.push(<a key={out.length} href={href} target={href.startsWith('http') ? '_blank' : undefined} rel="noopener noreferrer">{renderInline(label)}</a>);
            i = closePar + 1;
            continue;
          }
        }
      }
    }
    buf += text[i];
    i++;
  }
  flush();
  return out;
}

const ErrorBanner = ({ children }) => (
  <div style={{padding:'10px 12px',background:'#FCE6E0',color:'#A20235',borderRadius:8,fontSize:12.5,wordBreak:'break-word'}}>
    {children}
  </div>
);

/* ─── Strategy cascade modal ─────────────────────────────────────── */

const IMPACT_COLOR = {
  high:   { bg: '#FCE6E0', fg: '#A20235' },
  medium: { bg: '#FFE7DC', fg: '#B83C0F' },
  low:    { bg: '#DCF1E2', fg: '#00702A' },
};
const TARGET_ICON = {
  function:  'profile',
  agent:     'cog',
  service:   'briefcase',
  industry:  'compass',
  client:    'briefcase',
  orgrepo:   'file',
  people:    'profile',
};

const CascadeAnalyzeModal = ({ path, oldContent, newContent, dirty, onClose, onApproveAndCommit }) => {
  const [busy, setBusy] = React.useState(false);
  const [result, setResult] = React.useState(null);
  const [error, setError] = React.useState(null);
  const [committing, setCommitting] = React.useState(false);

  const analyze = async () => {
    setBusy(true); setError(null); setResult(null);
    try {
      const r = await analyzeStrategyCascade({ path, oldContent, newContent });
      setResult(r);
    } catch (err) {
      setError(err.message);
    } finally {
      setBusy(false);
    }
  };

  // Auto-run on open if the editor is dirty (the most common case).
  React.useEffect(() => { if (dirty) analyze(); /* eslint-disable-line */ }, []);

  // Phase B: each item in the response now also has a persisted
  // proposal id + status. The Apply / Dismiss buttons mutate state
  // optimistically via the per-item callback and update on success.
  const proposals = result?.proposals || [];
  const onProposalUpdated = updated => {
    setResult(r => r ? ({
      ...r,
      proposals: (r.proposals || []).map(p => p.id === updated.id ? updated : p),
    }) : r);
  };
  const grouped = proposals.reduce((acc, p) => { (acc[p.targetType] = acc[p.targetType] || []).push(p); return acc; }, {});

  return (
    <div className="modal-mask" onClick={onClose}>
      <div className="modal" onClick={e => e.stopPropagation()} style={{position:'relative',maxWidth:840,width:'95%'}}>
        <button className="modal__close" onClick={onClose}>×</button>
        <div style={{fontSize:11,letterSpacing:'0.08em',textTransform:'uppercase',color:'var(--orange)',marginBottom:6,fontWeight:600}}>Strategy cascade</div>
        <h3 style={{marginBottom:6}}>What else in the firm should change?</h3>
        <p style={{fontSize:13.5,color:'var(--fg-muted)',lineHeight:1.5,marginBottom:14}}>
          Claude reads the diff for <code style={{fontFamily:'JetBrains Mono',fontSize:11.5}}>{path}</code> and the firm graph
          (functions, services, industries, clients), and proposes which entities are affected. Admins can Apply each item —
          the suggestion appends to the target entity's description.
        </p>

        {!dirty && !result && (
          <div style={{padding:10,marginBottom:14,background:'var(--bg-alt)',borderRadius:8,fontSize:12,color:'var(--fg-muted)'}}>
            You haven't modified this file in this session. Click <strong>Analyze</strong> to ask Claude to look at the current saved version vs itself (will produce an empty result) — or edit first.
          </div>
        )}

        {error && <div style={{padding:10,marginBottom:14,background:'#FCE6E0',color:'#A20235',borderRadius:8,fontSize:13}}>{error}</div>}

        {busy && (
          <div style={{padding:24,textAlign:'center',color:'var(--fg-muted)',fontSize:13}}>
            <span className="live-dot" style={{marginRight:8}}/>
            Analyzing cascade — ~5–15s
          </div>
        )}

        {result && !busy && (
          <>
            {result.summary && (
              <div style={{padding:12,marginBottom:14,background:'var(--bg-alt)',borderRadius:8,fontSize:13.5,lineHeight:1.55}}>
                <div style={{fontSize:11,letterSpacing:'0.08em',textTransform:'uppercase',color:'var(--fg-muted)',marginBottom:4,fontWeight:600}}>Summary</div>
                {result.summary}
              </div>
            )}

            {proposals.length === 0 && (
              <div style={{padding:14,background:'var(--bg-alt)',borderRadius:8,fontSize:13,color:'var(--fg-muted)'}}>
                No cascade items proposed. The diff didn't touch anything that maps to a known function/service/industry/client/agent.
              </div>
            )}

            {proposals.length > 0 && (
              <div style={{padding:10,marginBottom:10,background:'var(--bg-alt)',borderRadius:8,fontSize:11.5,color:'var(--fg-muted)'}}>
                {(() => {
                  const counts = proposals.reduce((a, p) => ({ ...a, [p.status]: (a[p.status] || 0) + 1 }), {});
                  const parts = [];
                  if (counts.pending)   parts.push(`${counts.pending} pending`);
                  if (counts.applied)   parts.push(`${counts.applied} applied`);
                  if (counts.dismissed) parts.push(`${counts.dismissed} dismissed`);
                  return parts.join(' · ');
                })()}
              </div>
            )}

            {proposals.length > 0 && Object.entries(grouped).map(([type, list]) => (
              <div key={type} style={{marginBottom:14}}>
                <div style={{fontSize:11,letterSpacing:'0.08em',textTransform:'uppercase',color:'var(--fg-muted)',marginBottom:6,fontWeight:600,display:'flex',alignItems:'center',gap:6}}>
                  <Icon name={TARGET_ICON[type] || 'sparkle'} size={11}/>
                  {type}s · {list.length}
                </div>
                <div style={{display:'flex',flexDirection:'column',gap:6}}>
                  {list.map(p => <CascadeItemCard key={p.id} p={p} onUpdated={onProposalUpdated}/>)}
                </div>
              </div>
            ))}
          </>
        )}

        <div style={{display:'flex',gap:8,justifyContent:'flex-end',marginTop:14,alignItems:'center',flexWrap:'wrap'}}>
          {dirty && onApproveAndCommit && (
            <span style={{fontSize:11.5,color:'var(--fg-muted)',marginRight:'auto'}}>
              Approve & commit writes the file change. Cascade proposals stay pending until you Apply each.
            </span>
          )}
          <button type="button" className="btn-ghost" onClick={onClose} disabled={committing}>Close</button>
          <button type="button" className="btn-ghost" disabled={busy || committing} onClick={analyze}>
            {busy ? 'Analyzing…' : (result ? 'Re-analyze' : 'Analyze')}
          </button>
          {dirty && onApproveAndCommit && (
            <button
              type="button"
              className="btn-orange"
              disabled={busy || committing}
              onClick={async () => {
                setCommitting(true);
                try { await onApproveAndCommit(); }
                finally { setCommitting(false); }
              }}
              title="Commit the file change. Cascade items remain pending so admins can Apply them individually."
            >
              {committing ? 'Committing…' : 'Approve & commit'}
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

const CascadeItemCard = ({ p, onUpdated }) => {
  const impactC = IMPACT_COLOR[p.impact] || IMPACT_COLOR.medium;
  const [busy, setBusy] = React.useState(false);
  const [error, setError] = React.useState(null);

  const apply = async () => {
    setBusy(true); setError(null);
    try {
      const next = await applyChangeProposal(p.id);
      if (next) onUpdated(next);
    } catch (err) {
      setError(err.message);
    } finally {
      setBusy(false);
    }
  };
  const dismiss = async () => {
    if (!confirm(`Dismiss this proposal? It stays in the history but won't be applied.`)) return;
    setBusy(true); setError(null);
    try {
      const next = await dismissChangeProposal(p.id);
      if (next) onUpdated(next);
    } catch (err) {
      setError(err.message);
    } finally {
      setBusy(false);
    }
  };

  const isPending = p.status === 'pending';
  const isApplied = p.status === 'applied';
  const isDismissed = p.status === 'dismissed';
  const border = isApplied   ? '2px solid var(--support-green)'
               : isDismissed ? '1px dashed var(--border)'
               : '1px solid var(--border)';
  const bg     = isDismissed ? 'var(--bg-alt)' : 'var(--bg)';

  return (
    <div style={{padding:10,border,borderRadius:8,background:bg,opacity: isDismissed ? 0.7 : 1}}>
      <div style={{display:'flex',alignItems:'center',gap:8,marginBottom:6}}>
        <strong style={{fontSize:13.5}}>{p.targetLabel}</strong>
        <span style={{fontSize:10.5,padding:'1px 7px',borderRadius:4,background:impactC.bg,color:impactC.fg,textTransform:'uppercase',letterSpacing:'0.04em',fontWeight:600}}>{p.impact}</span>
        {isApplied && <span style={{fontSize:10.5,padding:'1px 7px',borderRadius:4,background:'#DCF1E2',color:'#00702A',textTransform:'uppercase',letterSpacing:'0.04em',fontWeight:600}}>applied</span>}
        {isDismissed && <span style={{fontSize:10.5,padding:'1px 7px',borderRadius:4,background:'var(--bg-card)',color:'var(--fg-muted)',textTransform:'uppercase',letterSpacing:'0.04em',fontWeight:600}}>dismissed</span>}
        {p.targetId && <span style={{marginLeft:'auto',fontSize:10,color:'var(--fg-muted)',fontFamily:'JetBrains Mono'}}>{p.targetId.slice(0, 8)}</span>}
      </div>
      <div style={{fontSize:12.5,color:'var(--fg-muted)',marginBottom:4,lineHeight:1.5}}>{p.rationale}</div>
      <div style={{fontSize:13,lineHeight:1.5}}><strong style={{color:'var(--orange)'}}>→</strong> {p.suggestion}</div>

      {error && <div style={{padding:8,marginTop:6,background:'#FCE6E0',color:'#A20235',borderRadius:6,fontSize:11.5}}>{error}</div>}

      {isPending && (
        <div style={{display:'flex',gap:6,marginTop:8,alignItems:'center'}}>
          {p.autoApplicable ? (
            <button type="button" className="btn-orange" style={{padding:'3px 10px',fontSize:11.5}} disabled={busy} onClick={apply}>
              {busy ? '…' : 'Apply'}
            </button>
          ) : (
            <span style={{fontSize:11,color:'var(--fg-muted)',fontStyle:'italic'}} title={`Auto-apply isn't supported for "${p.targetType}" — edit the source manually.`}>
              Manual change · dismiss when handled
            </span>
          )}
          <button type="button" className="btn-ghost" style={{padding:'3px 10px',fontSize:11.5}} disabled={busy} onClick={dismiss}>
            Dismiss
          </button>
          {p.autoApplicable && (
            <span style={{fontSize:11,color:'var(--fg-muted)',marginLeft:'auto'}}>
              Apply appends to {p.targetType}.description
            </span>
          )}
        </div>
      )}
    </div>
  );
};

/* ─── News (firm activity feed) ──────────────────────────────────── */

// Maps the dotted audit-log action keys onto a small set of categories
// the UI groups + filters by. Anything missing falls into "other".
const ACTION_CATEGORY = {
  'intent.created': 'intents', 'intent.planned': 'intents',
  'intent.run-started': 'intents', 'human-task.answered': 'intents',
  'worker.spawned': 'workers',
  'agent.manual-run': 'agents',
  'agent.proposal-applied': 'agents', 'agent.proposal-dismissed': 'agents',
  'cascade.proposal-applied': 'strategy', 'cascade.proposal-dismissed': 'strategy',
  'strategy.cascade-analyzed': 'strategy',
  'orgrepo.edited': 'orgrepo',
  'function.created': 'taxonomy', 'function.updated': 'taxonomy', 'function.deleted': 'taxonomy',
  'function.member-added': 'taxonomy', 'function.member-removed': 'taxonomy',
  'service.created': 'taxonomy', 'service.updated': 'taxonomy', 'service.deleted': 'taxonomy',
  'industry.created': 'taxonomy', 'industry.updated': 'taxonomy', 'industry.deleted': 'taxonomy',
  'client.created': 'taxonomy', 'client.updated': 'taxonomy', 'client.deleted': 'taxonomy',
};
const CATEGORY_META = {
  intents:   { label: 'Intents',     icon: 'sparkle',   color: 'var(--orange)' },
  agents:    { label: 'Agents',      icon: 'cog',       color: '#7B61FF' },
  strategy:  { label: 'Strategy',    icon: 'compass',   color: '#A20235' },
  orgrepo:   { label: 'OrgRepo',     icon: 'book',      color: '#1F6FEB' },
  workers:   { label: 'Workers',     icon: 'briefcase', color: '#0F8C70' },
  taxonomy:  { label: 'Taxonomy',    icon: 'profile',   color: 'var(--fg-muted)' },
  other:     { label: 'Other',       icon: 'file',      color: 'var(--fg-muted)' },
};

const ACTION_VERB = {
  'intent.created':            'opened a new intent',
  'intent.planned':            'finalised a plan for',
  'intent.run-started':        'started a run on',
  'human-task.answered':       'answered a human task in',
  'worker.spawned':            'spawned a worker',
  'agent.manual-run':          'ran a learning agent on',
  'agent.proposal-applied':    'applied a proposal from',
  'agent.proposal-dismissed':  'dismissed a proposal from',
  'cascade.proposal-applied':  'cascaded a change into',
  'cascade.proposal-dismissed':'dismissed a cascade item for',
  'strategy.cascade-analyzed': 'analysed the strategy cascade of',
  'orgrepo.edited':            'committed',
  'function.created':          'created the function',
  'function.updated':          'updated the function',
  'function.deleted':          'deleted the function',
  'function.member-added':     'added a member to',
  'function.member-removed':   'removed a member from',
  'service.created':           'created the service',
  'service.updated':           'updated the service',
  'service.deleted':           'deleted the service',
  'industry.created':          'created the industry',
  'industry.updated':          'updated the industry',
  'industry.deleted':          'deleted the industry',
  'client.created':            'added the client',
  'client.updated':            'updated the client',
  'client.deleted':            'removed the client',
};

const SEVERITY_C = {
  info:   { bg: 'transparent', fg: 'var(--fg-muted)' },
  medium: { bg: '#FFE7DC',     fg: '#B83C0F' },
  high:   { bg: '#FCE6E0',     fg: '#A20235' },
};

const FEED_FILTERS = [
  { id: 'all',       label: 'All activity' },
  { id: 'intents',   label: 'Intents' },
  { id: 'agents',    label: 'Agents' },
  { id: 'strategy',  label: 'Strategy' },
  { id: 'orgrepo',   label: 'OrgRepo' },
  { id: 'workers',   label: 'Workers' },
  { id: 'taxonomy',  label: 'Taxonomy' },
];

function timeAgo(date) {
  const d = date instanceof Date ? date : new Date(date);
  const sec = Math.max(0, Math.floor((Date.now() - d.getTime()) / 1000));
  if (sec < 60)         return `${sec}s ago`;
  if (sec < 3600)       return `${Math.floor(sec / 60)}m ago`;
  if (sec < 86400)      return `${Math.floor(sec / 3600)}h ago`;
  if (sec < 86400 * 7)  return `${Math.floor(sec / 86400)}d ago`;
  return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
}

export const NewsView = () => {
  const [entries, setEntries] = React.useState([]);
  const [loading, setLoading] = React.useState(true);
  const [error, setError]     = React.useState(null);
  const [filter, setFilter]   = React.useState('all');

  const reload = React.useCallback(() => {
    setLoading(true);
    setError(null);
    listFirmFeed(120)
      .then(setEntries)
      .catch(err => setError(err.message))
      .finally(() => setLoading(false));
  }, []);

  React.useEffect(() => { reload(); }, [reload]);
  // Poll lightly so the feed stays live without a refresh.
  React.useEffect(() => {
    const t = setInterval(reload, 60_000);
    return () => clearInterval(t);
  }, [reload]);

  const decorated = entries.map(e => ({
    ...e,
    category: ACTION_CATEGORY[e.action] || 'other',
  }));
  const filtered = filter === 'all' ? decorated : decorated.filter(e => e.category === filter);

  // Right-rail counters: how many of each category in the last 24h.
  const last24h = Date.now() - 86_400_000;
  const counters = decorated.reduce((acc, e) => {
    const t = e.createdAt instanceof Date ? e.createdAt.getTime() : new Date(e.createdAt).getTime();
    if (t >= last24h) acc[e.category] = (acc[e.category] || 0) + 1;
    return acc;
  }, {});
  const totalLast24h = Object.values(counters).reduce((a, b) => a + b, 0);

  // Top actors (by event count) in the visible window.
  const actorCounts = decorated.reduce((acc, e) => {
    const name = e.actor?.name || 'system';
    acc[name] = (acc[name] || 0) + 1;
    return acc;
  }, {});
  const topActors = Object.entries(actorCounts).sort((a, b) => b[1] - a[1]).slice(0, 6);

  return (
    <div className="page">
      <div className="page__head">
        <h1>Firm activity</h1>
        <div className="lede">Every state-changing event in eOS, live. Intents, agent runs, OrgRepo edits, workers, taxonomy changes — everything the firm just did.</div>
      </div>

      <div style={{display:'flex',gap:6,marginBottom:18,flexWrap:'wrap'}}>
        {FEED_FILTERS.map(f => (
          <span
            key={f.id}
            className={`tag ${filter === f.id ? 'tag--orange' : ''}`}
            style={{cursor:'pointer'}}
            onClick={() => setFilter(f.id)}
          >
            {f.label}
          </span>
        ))}
      </div>

      <div className="respo-sidebar" style={{gap:24}}>
        <div>
          {loading && <div style={{padding:60,textAlign:'center',color:'var(--fg-muted)',fontSize:13}}>Loading firm activity…</div>}
          {error && <ErrorBanner>{error}</ErrorBanner>}
          {!loading && !error && filtered.length === 0 && (
            <div style={{padding:24,background:'var(--bg-alt)',borderRadius:12,color:'var(--fg-muted)',fontSize:13.5,textAlign:'center'}}>
              No activity yet. The firm log fills up as people use intents, OrgRepo, workers, and agents.
            </div>
          )}
          {!loading && !error && filtered.map(e => <FeedCard key={e.id} entry={e}/>)}
        </div>

        <div>
          <div style={{padding:18,background:'var(--bg-alt)',borderRadius:14,marginBottom:14}}>
            <div style={{fontSize:11,letterSpacing:'0.08em',textTransform:'uppercase',color:'var(--fg-muted)',marginBottom:10,fontWeight:600}}>Last 24 hours</div>
            <div style={{fontFamily:'var(--font-display)',fontSize:32,lineHeight:1,marginBottom:14}}>{totalLast24h}<span style={{fontSize:12,fontFamily:'inherit',color:'var(--fg-muted)',marginLeft:6}}>events</span></div>
            <div style={{display:'flex',flexDirection:'column',gap:6}}>
              {Object.keys(CATEGORY_META).filter(k => k !== 'other' && counters[k]).map(k => {
                const m = CATEGORY_META[k];
                return (
                  <div key={k} style={{display:'flex',alignItems:'center',gap:8,fontSize:12.5}}>
                    <Icon name={m.icon} size={12}/>
                    <span>{m.label}</span>
                    <span style={{marginLeft:'auto',fontFamily:'JetBrains Mono',color:m.color,fontWeight:600}}>{counters[k]}</span>
                  </div>
                );
              })}
              {totalLast24h === 0 && <div style={{fontSize:12,color:'var(--fg-muted)',fontStyle:'italic'}}>Nothing in the last day.</div>}
            </div>
          </div>

          <div style={{padding:18,background:'var(--bg-alt)',borderRadius:14}}>
            <div style={{fontSize:11,letterSpacing:'0.08em',textTransform:'uppercase',color:'var(--fg-muted)',marginBottom:10,fontWeight:600}}>Most active</div>
            <div style={{display:'flex',flexDirection:'column',gap:6}}>
              {topActors.length === 0 && <div style={{fontSize:12,color:'var(--fg-muted)',fontStyle:'italic'}}>No actors yet.</div>}
              {topActors.map(([name, n]) => (
                <div key={name} style={{display:'flex',alignItems:'center',gap:8,fontSize:12.5}}>
                  <Icon name="profile" size={12}/>
                  <span style={{overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap'}}>{name}</span>
                  <span style={{marginLeft:'auto',fontFamily:'JetBrains Mono',color:'var(--fg-muted)'}}>{n}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

const FeedCard = ({ entry }) => {
  const cat = ACTION_CATEGORY[entry.action] || 'other';
  const meta = CATEGORY_META[cat];
  const verb = ACTION_VERB[entry.action] || entry.action;
  const sev  = SEVERITY_C[entry.severity] || SEVERITY_C.info;
  const target = entry.target;

  return (
    <div className="news-card" style={{borderLeft:`3px solid ${meta.color}`}}>
      <div style={{display:'flex',alignItems:'center',gap:8,marginBottom:8}}>
        <span style={{fontSize:10.5,letterSpacing:'0.08em',textTransform:'uppercase',color:meta.color,fontWeight:600,display:'inline-flex',alignItems:'center',gap:4}}>
          <Icon name={meta.icon} size={11}/>
          {meta.label}
        </span>
        {entry.severity !== 'info' && (
          <span style={{fontSize:10,padding:'1px 6px',borderRadius:4,background:sev.bg,color:sev.fg,textTransform:'uppercase',letterSpacing:'0.06em',fontWeight:600}}>
            {entry.severity}
          </span>
        )}
        <span style={{marginLeft:'auto',fontSize:11,color:'var(--fg-muted)'}}>{timeAgo(entry.createdAt)}</span>
      </div>
      <div style={{fontSize:14.5,lineHeight:1.45}}>
        <strong>{entry.actor?.name || 'system'}</strong>{' '}
        <span style={{color:'var(--fg-muted)'}}>{verb}</span>
        {target?.label && (
          <>
            {' '}
            <span style={{fontFamily: target.type === 'orgrepo' ? 'JetBrains Mono' : 'inherit',fontSize: target.type === 'orgrepo' ? 13 : 14.5}}>
              {target.label}
            </span>
          </>
        )}
        {target?.type && !target.label && <span style={{color:'var(--fg-muted)'}}> {target.type}</span>}
      </div>
      {entry.details && Object.keys(entry.details).length > 0 && (
        <FeedCardDetails details={entry.details}/>
      )}
    </div>
  );
};

const FeedCardDetails = ({ details }) => {
  // Tiny key=value summary — keeps cards informative without dumping JSON.
  const pairs = Object.entries(details)
    .filter(([, v]) => v !== null && v !== undefined && typeof v !== 'object')
    .slice(0, 4);
  if (pairs.length === 0) return null;
  return (
    <div style={{marginTop:8,fontSize:11.5,color:'var(--fg-muted)',fontFamily:'JetBrains Mono',display:'flex',flexWrap:'wrap',gap:'6px 14px'}}>
      {pairs.map(([k, v]) => (
        <span key={k}><span style={{opacity:0.6}}>{k}=</span>{String(v)}</span>
      ))}
    </div>
  );
};

/* ─── Reports / artifacts ────────────────────────────────────────── */

// The firm's real "produced artifacts" live in OrgRepo as markdown:
// /intents/* (specs filed when an intent is planned) and /logs/*
// (completion writeups). Everything else in OrgRepo is canon
// reference (guidelines, governance, roles, skills, content,
// processes). We surface both lenses here.
const REPORT_FOLDERS = [
  { folder: '/intents',    label: 'Intents',     icon: 'sparkle',   color: 'var(--orange)' },
  { folder: '/audit',      label: 'Run logs',    icon: 'time',      color: '#0F8C70' },
  { folder: '/rules',      label: 'Rules',       icon: 'shield',    color: '#A20235' },
  { folder: '/actors',     label: 'Actors',      icon: 'profile',   color: '#7B61FF' },
  { folder: '/skills',     label: 'Skills',      icon: 'sparkle',   color: '#1F6FEB' },
  { folder: '/sources',    label: 'Sources',     icon: 'book',      color: '#1E7A45' },
  { folder: '/artifacts',  label: 'Artifacts',   icon: 'book',      color: '#C8501E' },
];

function approxSize(content) {
  if (!content) return '—';
  return fmtBytes(new Blob([content]).size);
}

function fmtBytes(bytes) {
  if (!Number.isFinite(bytes)) return '—';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / 1024 / 1024).toFixed(1)} MB`;
}

function fileTitle(file) {
  // Pull the first markdown H1 if present, otherwise fall back to the filename.
  const m = (file.content || '').match(/^\s*#\s+(.+?)\s*$/m);
  if (m) return m[1];
  const base = file.path.split('/').pop().replace(/\.md$/i, '');
  return base.replace(/[-_]+/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
}

/* ─── My runs ────────────────────────────────────────────────────────
 * The viewer's OWN agent runs (custom agents only — no master/system
 * activity), sourced from /api/me/runs (owner_user_id scoped). This is
 * the per-user counterpart to the shared /logs journals, which can't be
 * split by owner. Each row deep-links to the run's detail on the Agents
 * page (#agents/<agentId>/<runId>). Flat list, hairline rows — matches
 * the light detail-page style. */
const RUN_STATUS_META = {
  done:    { label: 'Done',    color: '#1A6A2B', bg: '#DCF1E2' },
  failed:  { label: 'Failed',  color: '#A20235', bg: '#FCE6E0' },
  running: { label: 'Running', color: '#B83C0F', bg: '#FCEFE0' },
};
const MyRunsSection = ({ runs }) => {
  const openRun = (r) => {
    // A run shared with me (I'm not its owner) → the agent-detail run view
    // is owner-scoped and would 404, so deep-link straight to the deck
    // viewer, where my collaborator access applies.
    if (r.sharedByName) {
      location.hash = `runs/${encodeURIComponent(r.id)}/deck?agent=${encodeURIComponent(r.agentId)}`;
      return;
    }
    location.hash = `agents/${encodeURIComponent(r.agentId)}/${encodeURIComponent(r.id)}`;
  };
  return (
    <div style={{marginBottom:24}}>
      <div style={{fontSize:10.5,letterSpacing:'0.08em',textTransform:'uppercase',color:'var(--fg-muted)',fontWeight:600,marginBottom:8}}>
        My runs
      </div>
      <div>
        {runs.map(r => {
          const meta = RUN_STATUS_META[r.status] || RUN_STATUS_META.running;
          const when = r.completedAt || r.startedAt;
          return (
            <div
              key={r.id}
              onClick={() => openRun(r)}
              style={{display:'flex',alignItems:'center',gap:12,padding:'10px 2px',borderBottom:'1px solid var(--border)',cursor:'pointer'}}
            >
              <span style={{fontSize:10,padding:'2px 8px',borderRadius:10,fontWeight:600,letterSpacing:'0.04em',textTransform:'uppercase',background:meta.bg,color:meta.color,flexShrink:0,minWidth:62,textAlign:'center'}}>
                {meta.label}
              </span>
              <div style={{minWidth:0,flex:1}}>
                <div style={{fontSize:13.5,lineHeight:1.3,whiteSpace:'nowrap',overflow:'hidden',textOverflow:'ellipsis'}}>
                  {r.title}
                  {r.sharedByName && (
                    <span style={{marginLeft:8,fontSize:10,padding:'1px 7px',borderRadius:10,fontWeight:600,letterSpacing:'0.04em',textTransform:'uppercase',background:'#E6E0F4',color:'#3D2B6E'}}>
                      Shared
                    </span>
                  )}
                </div>
                <div style={{fontSize:11.5,color:'var(--fg-muted)',marginTop:1}}>
                  {r.agentLabel}{r.sharedByName ? ` · shared by ${r.sharedByName}` : ''}
                </div>
              </div>
              <span style={{fontSize:11,color:'var(--fg-muted)',flexShrink:0}}>
                {when ? timeAgo(new Date(when)) : ''}
              </span>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export const ReportsView = () => {
  const [tree, setTree] = React.useState([]);
  const [attachments, setAttachments] = React.useState([]);
  const [pdfs, setPdfs] = React.useState([]);
  const [runs, setRuns] = React.useState([]);
  const [filter, setFilter] = React.useState('all');
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState(null);
  const [openFile, setOpenFile] = React.useState(null);

  const reload = React.useCallback(() => {
    setLoading(true);
    setError(null);
    return Promise.all([
      getOrgRepoTree().catch(err => { throw err; }),
      listMyArtifacts().catch(() => []),
      listMyGeneratedPdfs().catch(() => []),
      listMyRuns().catch(() => []),
    ])
      .then(([{ tree }, atts, generatedPdfs, myRuns]) => {
        setTree(tree || []);
        setAttachments(atts || []);
        setPdfs(generatedPdfs || []);
        setRuns(myRuns || []);
      })
      .catch(err => setError(err.message))
      .finally(() => setLoading(false));
  }, []);

  React.useEffect(() => { reload(); }, [reload]);

  // Lightweight poll for the two live surfaces (generated PDFs + my runs)
  // — when a user lands here expecting a fresh artifact or run-status flip
  // from an in-flight intent, they shouldn't have to manually refresh.
  // Skips the full OrgRepo tree, which rarely changes mid-session.
  React.useEffect(() => {
    const t = setInterval(() => {
      listMyGeneratedPdfs().then(p => setPdfs(p || [])).catch(() => {});
      listMyRuns().then(r => setRuns(r || [])).catch(() => {});
    }, 12_000);
    return () => clearInterval(t);
  }, []);

  const onDeleteFile = async path => {
    if (!window.confirm(`Delete ${path}? This removes the file from OrgRepo. Cannot be undone.`)) return;
    try {
      await deleteOrgRepoFile(path);
      await reload();
    } catch (err) {
      setError(err.message);
    }
  };

  // Per-item delete — works for both chat uploads and generated
  // binaries (the DELETE endpoint falls back to an R2-only delete when
  // the key isn't referenced by any message).
  const onDeleteArtifact = async item => {
    if (!window.confirm(`Delete "${item.title}"? This permanently removes the file. This cannot be undone.`)) return;
    try {
      await deleteMyArtifact(item.id);
      await reload();
    } catch (err) {
      setError(err.message);
    }
  };

  const onClearArtifacts = async () => {
    if (!window.confirm('Clear ALL your artifacts? This permanently deletes every generated file and uploaded attachment shown here. This cannot be undone.')) return;
    try {
      await clearAllMyArtifacts();
      await reload();
    } catch (err) {
      setError(err.message);
    }
  };

  // Only files inside known REPORT_FOLDERS land in this view; orgrepo
  // can grow other top-level dirs that don't make sense as "reports".
  const knownFolders = new Set(REPORT_FOLDERS.map(f => f.folder));
  const all = tree.filter(f => {
    const top = '/' + f.path.split('/')[1];
    return knownFolders.has(top);
  });
  const visible = filter === 'all' ? all : all.filter(f => f.path.startsWith(filter + '/'));

  // Sort newest-first. The tree endpoint serialises updatedAt as an
  // ISO date string, so coerce to a timestamp before subtracting.
  const ts = x => (x ? new Date(x).getTime() : 0);
  const sorted = [...visible].sort((a, b) => ts(b.updatedAt) - ts(a.updatedAt));

  const counts = REPORT_FOLDERS.map(meta => ({
    ...meta,
    n: all.filter(f => f.path.startsWith(meta.folder + '/')).length,
  }));

  return (
    <div className="page">
      <div className="page__head">
        <h1>Artifacts & reports</h1>
        <div className="lede">Everything eOS has produced and curates as markdown in OrgRepo — intents, agents, run logs, guidelines, governance, roles, skills, content. Click any file to open.</div>
      </div>

      <div style={{display:'flex',gap:6,marginBottom:18,flexWrap:'wrap'}}>
        <span className={`tag ${filter === 'all' ? 'tag--orange' : ''}`} style={{cursor:'pointer'}} onClick={() => setFilter('all')}>
          All ({all.length + pdfs.length + attachments.length + runs.length})
        </span>
        {runs.length > 0 && (
          <span
            className={`tag ${filter === '_runs' ? 'tag--orange' : ''}`}
            style={{cursor:'pointer'}}
            onClick={() => setFilter('_runs')}
          >
            My runs ({runs.length})
          </span>
        )}
        {(pdfs.length > 0 || attachments.length > 0) && (
          <span
            className={`tag ${filter === '_artifacts' ? 'tag--orange' : ''}`}
            style={{cursor:'pointer'}}
            onClick={() => setFilter('_artifacts')}
          >
            Artifacts ({pdfs.length + attachments.length})
          </span>
        )}
        {counts.filter(c => c.n > 0).map(c => (
          <span
            key={c.folder}
            className={`tag ${filter === c.folder ? 'tag--orange' : ''}`}
            style={{cursor:'pointer'}}
            onClick={() => setFilter(c.folder)}
          >
            {c.label} ({c.n})
          </span>
        ))}
      </div>

      {loading && <div style={{padding:60,textAlign:'center',color:'var(--fg-muted)',fontSize:13}}>Loading OrgRepo…</div>}
      {error && <ErrorBanner>{error}</ErrorBanner>}

      {!loading && !error && sorted.length === 0 && attachments.length === 0 && pdfs.length === 0 && runs.length === 0 && (
        <div style={{padding:24,background:'var(--bg-alt)',borderRadius:12,color:'var(--fg-muted)',fontSize:13.5,textAlign:'center'}}>
          No artifacts yet. Create an intent — its spec writes to <code>/intents</code>, and the run log lands in <code>/logs</code> when it completes.
        </div>
      )}

      {!loading && !error && runs.length > 0 && (filter === 'all' || filter === '_runs') && (
        <MyRunsSection runs={runs}/>
      )}

      {!loading && !error && (pdfs.length > 0 || attachments.length > 0) && (filter === 'all' || filter === '_artifacts') && (
        <ArtifactsSection pdfs={pdfs} attachments={attachments} onDeleteArtifact={onDeleteArtifact} onClearArtifacts={onClearArtifacts}/>
      )}

      {!loading && !error && sorted.length > 0 && filter !== '_artifacts' && filter !== '_runs' && (
        <div className="tile-grid">
          {sorted.map(f => {
            const top = '/' + f.path.split('/')[1];
            const meta = REPORT_FOLDERS.find(m => m.folder === top) || { label: top, color: 'var(--fg-muted)', icon: 'file' };
            return (
              <div
                key={f.path}
                className="kpi"
                style={{cursor:'pointer',position:'relative',borderLeft:`3px solid ${meta.color}`}}
                onClick={() => setOpenFile(f.path)}
              >
                <div style={{display:'flex',alignItems:'center',gap:8,marginBottom:12,paddingRight:24}}>
                  <Icon name={meta.icon} size={12}/>
                  <span style={{fontSize:10.5,letterSpacing:'0.08em',textTransform:'uppercase',color:meta.color,fontWeight:600}}>{meta.label}</span>
                  <span style={{fontSize:11,color:'var(--fg-muted)',marginLeft:'auto'}}>
                    {f.updatedAt ? timeAgo(new Date(f.updatedAt)) : ''}
                  </span>
                </div>
                <div style={{fontFamily:'var(--font-display)',fontSize:18,lineHeight:1.2,marginBottom:6}}>{fileTitle(f)}</div>
                <div style={{fontSize:11.5,color:'var(--fg-muted)',fontFamily:'JetBrains Mono'}}>{f.path}</div>
                <button
                  type="button"
                  title="Delete this file from OrgRepo"
                  onClick={e => { e.stopPropagation(); onDeleteFile(f.path); }}
                  style={{position:'absolute',top:8,right:8,padding:'2px 7px',fontSize:11,fontWeight:600,background:'var(--bg)',border:'1px solid var(--border)',borderRadius:6,cursor:'pointer',color:'#A20235'}}
                >✕</button>
              </div>
            );
          })}
        </div>
      )}

      {openFile && <ReportPreviewModal path={openFile} onClose={() => setOpenFile(null)}/>}
    </div>
  );
};

/* ────────────────────────────────────────────────────────────────────
 * Unified Artifacts section — replaces the two separate "Generated
 * PDFs" + "Chat uploads" lists. Top filter row: All / Chat-generated /
 * Agent-generated / Chat uploads. When Agent-generated is selected, a
 * second row of chips lists every agent name that produced an
 * artifact, so admins can drill into one agent at a time.
 *
 * Each artifact card carries a filetype icon, a self-speaking title
 * (never a UUID — the server-side deriveArtifactTitle helper guards
 * that), a small CSS preview, a "From chat / From agent" source line,
 * and Open / Show prompt / Delete buttons.
 * ──────────────────────────────────────────────────────────────────── */

const ARTIFACT_SOURCES = [
  { id: 'all',     label: 'All artefacts' },
  { id: 'chat',    label: 'Chat-generated' },
  { id: 'agent',   label: 'Agent-generated' },
  { id: 'upload',  label: 'Chat uploads' },
];

const ArtifactsSection = ({ pdfs, attachments, onDeleteArtifact, onClearArtifacts }) => {
  const [source, setSource] = React.useState('all');
  const [agentFilter, setAgentFilter] = React.useState(null);   // agentId | null
  const [promptOpen, setPromptOpen] = React.useState(null);     // { kind, ... } | null

  // Build a flat artifact list: generated PDFs + uploads, normalised.
  const items = React.useMemo(() => {
    const all = [];
    for (const p of pdfs) {
      const kind = p.source === 'agent' ? 'agent'
                 : p.source === 'chat'  ? 'chat'
                 : (p.intentId ? 'agent' : (p.chatId ? 'chat' : 'system'));
      const fmt = p.format || 'pdf';   // older API responses didn't carry format
      const mimeByFormat = {
        pdf:  'application/pdf',
        docx: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
        pptx: 'application/vnd.openxmlformats-officedocument.presentationml.presentation',
        xlsx: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      };
      all.push({
        id: p.key,
        type: fmt,
        kind,
        title: p.title,
        url: p.url,
        size: p.size,
        ext: fmt,
        mime: mimeByFormat[fmt] || 'application/octet-stream',
        at: p.generatedAt,
        chatId: p.chatId,
        chatTitle: p.chatTitle,
        messageId: p.messageId,
        agentId: p.agentId,
        agentName: p.agentName,
        intentId: p.intentId,
        userPrompt: p.userPrompt,
        teaser: p.teaser || null,
        previewKind: p.previewKind || null,
      });
    }
    for (const a of attachments) {
      all.push({
        id: a.key,
        type: 'upload',
        kind: 'upload',
        title: a.name || 'Untitled',
        url: a.url,
        size: a.size,
        ext: ((a.name || '').match(/\.([a-z0-9]+)$/i) || [, ''])[1].toLowerCase() || 'bin',
        mime: a.mime,
        at: a.uploadedAt,
        chatId: a.chatId,
        chatTitle: a.chatTitle,
        messageId: a.messageId,
        agentId: null,
        agentName: null,
        intentId: null,
        userPrompt: null,
        previewKind: null,
        _raw: a,
      });
    }
    return all.sort((x, y) => (y.at || 0) - (x.at || 0));
  }, [pdfs, attachments]);

  // Distinct agents that produced an artifact — populates the per-agent
  // chip row when source === 'agent'.
  const agentChips = React.useMemo(() => {
    const map = new Map();
    for (const it of items) {
      if (it.kind !== 'agent') continue;
      const key = it.agentId || it.agentName || '_unknown';
      if (!map.has(key)) map.set(key, { id: key, name: it.agentName || '(unknown agent)', count: 0 });
      map.get(key).count++;
    }
    return [...map.values()].sort((a, b) => b.count - a.count);
  }, [items]);

  const filtered = items.filter(it => {
    if (source === 'all') return true;
    if (source === 'upload') return it.kind === 'upload';
    if (source === 'chat')   return it.kind === 'chat';
    if (source === 'agent') {
      if (it.kind !== 'agent') return false;
      if (agentFilter) {
        const k = it.agentId || it.agentName || '_unknown';
        return k === agentFilter;
      }
      return true;
    }
    return true;
  });

  // Counts for the source pills.
  const counts = items.reduce((acc, it) => {
    acc.all++;
    if (it.kind === 'chat')   acc.chat++;
    if (it.kind === 'agent')  acc.agent++;
    if (it.kind === 'upload') acc.upload++;
    return acc;
  }, { all: 0, chat: 0, agent: 0, upload: 0 });

  return (
    <div style={{marginBottom:24}}>
      <div style={{display:'flex',alignItems:'center',gap:8,marginBottom:10,flexWrap:'wrap'}}>
        <div style={{fontSize:11,letterSpacing:'0.08em',textTransform:'uppercase',color:'var(--fg-muted)',fontWeight:600}}>
          Artifacts · {filtered.length}
        </div>
        <span style={{fontSize:10.5,color:'var(--fg-muted)'}}>auto-refreshes every 12s</span>
        <div style={{flex:1}}/>
        {items.length > 0 && (
          <button
            type="button"
            onClick={onClearArtifacts}
            title="Delete all your artifacts"
            style={{
              padding:'5px 10px',borderRadius:6,
              background:'var(--bg)',border:'1px solid var(--border)',
              fontSize:11.5,cursor:'pointer',color:'#A20235',fontWeight:600,
            }}
          >
            Clear all artifacts
          </button>
        )}
      </div>

      {/* Source filter row */}
      <div style={{display:'flex',gap:6,marginBottom:8,flexWrap:'wrap'}}>
        {ARTIFACT_SOURCES.map(s => (
          <span
            key={s.id}
            className={`tag ${source === s.id ? 'tag--orange' : ''}`}
            style={{cursor:'pointer'}}
            onClick={() => { setSource(s.id); setAgentFilter(null); }}
          >
            {s.label} ({s.id === 'all' ? counts.all : counts[s.id] || 0})
          </span>
        ))}
      </div>

      {/* Per-agent filter row — only shown when Agent-generated is selected */}
      {source === 'agent' && agentChips.length > 0 && (
        <div style={{display:'flex',gap:6,marginBottom:14,flexWrap:'wrap',paddingLeft:10,borderLeft:'2px solid var(--border)'}}>
          <span style={{fontSize:11,color:'var(--fg-muted)',alignSelf:'center'}}>Per agent:</span>
          <span
            className={`tag ${agentFilter === null ? 'tag--orange' : ''}`}
            style={{cursor:'pointer',fontSize:11}}
            onClick={() => setAgentFilter(null)}
          >
            All agents ({counts.agent})
          </span>
          {agentChips.map(a => (
            <span
              key={a.id}
              className={`tag ${agentFilter === a.id ? 'tag--orange' : ''}`}
              style={{cursor:'pointer',fontSize:11}}
              onClick={() => setAgentFilter(a.id)}
            >
              {a.name} ({a.count})
            </span>
          ))}
        </div>
      )}

      {filtered.length === 0 && (
        <div style={{padding:24,background:'var(--bg-alt)',borderRadius:12,color:'var(--fg-muted)',fontSize:13.5,textAlign:'center'}}>
          No artefacts in this view.
        </div>
      )}

      <div className="tile-grid">
        {filtered.map(it => (
          <ArtifactCard
            key={it.id}
            item={it}
            onShowPrompt={() => setPromptOpen(it)}
            onDelete={() => onDeleteArtifact(it)}
          />
        ))}
      </div>

      {promptOpen && (
        <ArtifactPromptModal item={promptOpen} onClose={() => setPromptOpen(null)}/>
      )}
    </div>
  );
};

/* Filetype glyph — terse SVG block with a coloured corner fold + label.
 * Falls back to a neutral grey for unknown extensions. */
function FiletypeIcon({ ext, size = 36 }) {
  const palette = {
    pdf:  { fill: '#A20235', label: 'PDF'  },
    pptx: { fill: '#D24726', label: 'PPT'  },
    ppt:  { fill: '#D24726', label: 'PPT'  },
    docx: { fill: '#2B579A', label: 'DOC'  },
    doc:  { fill: '#2B579A', label: 'DOC'  },
    xlsx: { fill: '#217346', label: 'XLS'  },
    xls:  { fill: '#217346', label: 'XLS'  },
    csv:  { fill: '#217346', label: 'CSV'  },
    png:  { fill: '#5C2D91', label: 'PNG'  },
    jpg:  { fill: '#5C2D91', label: 'JPG'  },
    jpeg: { fill: '#5C2D91', label: 'JPG'  },
    gif:  { fill: '#5C2D91', label: 'GIF'  },
    md:   { fill: '#3B2D8C', label: 'MD'   },
    txt:  { fill: '#6B6457', label: 'TXT'  },
    json: { fill: '#6B6457', label: 'JSON' },
  };
  const meta = palette[ext] || { fill: '#6B6457', label: (ext || 'BIN').toUpperCase().slice(0, 4) };
  return (
    <svg width={size} height={size * 1.2} viewBox="0 0 36 44" style={{flexShrink:0}}>
      <path
        d="M4 2h20l8 8v30a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2z"
        fill="var(--bg)" stroke={meta.fill} strokeWidth="1.5"
      />
      <path d="M24 2v8h8" fill={meta.fill} opacity="0.18"/>
      <path d="M24 2v8h8" fill="none" stroke={meta.fill} strokeWidth="1.5"/>
      <text
        x="18" y="32"
        textAnchor="middle"
        fontSize="9"
        fontWeight="700"
        fontFamily="ui-sans-serif, system-ui"
        fill={meta.fill}
      >
        {meta.label}
      </text>
    </svg>
  );
}

/* Resolve the on-demand HTML preview route for an artefact, or null.
 * The server (generated-pdfs.ts) sets item.previewKind only when a
 * content-store row actually backs the artefact, so this never points
 * at a 404. Agent-run artefacts key by intentId; chat artefacts by
 * messageId. */
function artifactPreviewBase(item) {
  if (!item.previewKind) return null;
  if (item.intentId)  return `/api/agents/_/runs/${encodeURIComponent(item.intentId)}/${item.previewKind}/preview`;
  if (item.messageId) return `/api/chat-artefacts/${encodeURIComponent(item.messageId)}/${item.previewKind}/preview`;
  return null;
}

/* First-page HTML preview thumbnail. Loads the chrome-less `?embed=1`
 * variant of the preview route into a sandboxed, non-interactive iframe;
 * the whole tile links to the full preview in a new tab. Decks render at
 * a 16:9 native slide; memos / sheets are flowing documents clipped to a
 * fixed height so the first section/sheet shows at the top. */
const ArtifactPreviewThumb = ({ item, previewBase, accent }) => {
  const isDeck = item.previewKind === 'deck';
  return (
    <a
      href={previewBase}
      target="_blank"
      rel="noopener noreferrer"
      title="Open full HTML preview"
      style={{ display:'block', textDecoration:'none', color:'inherit' }}
    >
      <div style={{
        position:'relative',
        width:'100%',
        ...(isDeck ? { aspectRatio:'16 / 9' } : { height:150 }),
        borderRadius:6,
        overflow:'hidden',
        border:'1px solid var(--border)',
        background:'#fff',
      }}>
        <iframe
          title={`${item.title} — first page preview`}
          src={`${previewBase}?embed=1`}
          loading="lazy"
          scrolling="no"
          sandbox="allow-same-origin allow-scripts"
          style={{
            position:'absolute', top:0, left:0,
            width:'100%',
            height: isDeck ? '100%' : 420,
            border:0,
            pointerEvents:'none',
          }}
        />
        <span style={{
          position:'absolute', top:6, left:6,
          fontSize:9.5, fontWeight:700, letterSpacing:'0.05em', textTransform:'uppercase',
          color:'#fff', background:`${accent}`, opacity:0.92,
          padding:'2px 7px', borderRadius:4,
        }}>
          First page
        </span>
      </div>
    </a>
  );
};

const ArtifactCard = ({ item, onShowPrompt, onDelete }) => {
  const kindColor = item.kind === 'agent'  ? '#3B2D8C'
                  : item.kind === 'chat'   ? '#A20235'
                  : item.kind === 'upload' ? '#FF6428' : '#6B6457';
  const kindLabel = item.kind === 'agent'  ? 'Agent-generated'
                  : item.kind === 'chat'   ? 'Chat-generated'
                  : item.kind === 'upload' ? 'Chat upload' : 'System';
  const sourceLine = item.kind === 'agent' && item.agentName
    ? `From agent: ${item.agentName}`
    : (item.chatTitle ? `From chat: ${item.chatTitle}` : null);
  const hasPrompt = !!(item.userPrompt || item.chatId || item.intentId);
  const previewBase = artifactPreviewBase(item);

  return (
    <div
      className="kpi"
      style={{
        position:'relative',
        borderLeft:`3px solid ${kindColor}`,
        display:'flex',flexDirection:'column',gap:8,
      }}
    >
      <div style={{display:'flex',alignItems:'center',gap:8}}>
        <span style={{fontSize:10.5,letterSpacing:'0.08em',textTransform:'uppercase',color:kindColor,fontWeight:600}}>
          {kindLabel}
        </span>
        <span style={{fontSize:11,color:'var(--fg-muted)',marginLeft:'auto'}}>{timeAgo(new Date(item.at))}</span>
      </div>

      {/* Header row — filetype glyph + title block. When the artefact
          has a renderable content store, a first-page HTML thumbnail is
          shown below (see ArtifactPreviewThumb); the glyph stays as the
          format marker + the fallback for artefacts with no preview. */}
      <div style={{display:'flex',gap:12,alignItems:'flex-start'}}>
        <FiletypeIcon ext={item.ext} size={40}/>
        <div style={{minWidth:0,flex:1}}>
          <div style={{
            fontFamily:'var(--font-display)',fontSize:15,lineHeight:1.25,
            wordBreak:'break-word',
            overflow:'hidden',textOverflow:'ellipsis',display:'-webkit-box',
            WebkitLineClamp:3,WebkitBoxOrient:'vertical',
          }}>{item.title}</div>
          <div style={{fontSize:11.5,color:'var(--fg-muted)',marginTop:4}}>
            {item.mime} · {fmtBytes(item.size)}
          </div>
        </div>
      </div>

      {sourceLine && (
        <div style={{fontSize:11.5,color:'var(--fg-muted)'}}>{sourceLine}</div>
      )}

      {/* First-page HTML preview when one is renderable; otherwise fall
          back to the plain-text teaser drawn from the same first page. */}
      {previewBase ? (
        <ArtifactPreviewThumb item={item} previewBase={previewBase} accent={kindColor}/>
      ) : item.teaser ? (
        <div style={{
          fontSize:12,
          lineHeight:1.5,
          color:'var(--fg)',
          padding:'8px 10px',
          background:'var(--bg-alt)',
          borderRadius:6,
          borderLeft:`2px solid ${kindColor}`,
          overflow:'hidden',
          display:'-webkit-box',
          WebkitLineClamp:3,
          WebkitBoxOrient:'vertical',
        }}>{item.teaser}</div>
      ) : null}

      <div style={{display:'flex',gap:6,marginTop:'auto',flexWrap:'wrap'}}>
        <a
          href={item.url}
          target="_blank"
          rel="noopener noreferrer"
          style={{
            flex:1,minWidth:90,padding:'7px 10px',borderRadius:6,
            background:kindColor,color:'#fff',textDecoration:'none',
            fontSize:12,fontWeight:600,textAlign:'center',
          }}
        >
          Open
        </a>
        {hasPrompt && (
          <button
            type="button"
            onClick={onShowPrompt}
            style={{
              padding:'7px 10px',borderRadius:6,
              background:'var(--bg)',border:'1px solid var(--border)',
              fontSize:12,cursor:'pointer',
            }}
          >
            Show prompt
          </button>
        )}
        {onDelete && (
          <button
            type="button"
            title="Delete this artifact"
            onClick={onDelete}
            style={{
              padding:'7px 10px',borderRadius:6,
              background:'var(--bg)',border:'1px solid var(--border)',
              fontSize:12,cursor:'pointer',color:'#A20235',
            }}
          >
            ✕
          </button>
        )}
      </div>
    </div>
  );
};

const ArtifactPromptModal = ({ item, onClose }) => {
  return (
    <div className="modal-mask" onClick={onClose}>
      <div className="modal" onClick={e => e.stopPropagation()} style={{maxWidth:680,width:'95%'}}>
        <button className="modal__close" onClick={onClose}>×</button>
        <div style={{display:'flex',alignItems:'center',gap:10,marginBottom:14}}>
          <FiletypeIcon ext={item.ext} size={32}/>
          <div style={{minWidth:0,flex:1}}>
            <div style={{fontFamily:'var(--font-display)',fontSize:18,lineHeight:1.25,wordBreak:'break-word'}}>{item.title}</div>
            <div style={{fontSize:11.5,color:'var(--fg-muted)',marginTop:2}}>
              {item.kind === 'agent' && item.agentName ? `From agent: ${item.agentName}` :
               item.chatTitle ? `From chat: ${item.chatTitle}` : 'No source link'}
            </div>
          </div>
        </div>
        {item.userPrompt ? (
          <div>
            <div style={{fontSize:10.5,letterSpacing:'0.08em',textTransform:'uppercase',color:'var(--fg-muted)',marginBottom:6,fontWeight:600}}>
              Originating prompt
            </div>
            <div style={{
              padding:12,background:'var(--bg-alt)',borderRadius:8,
              fontSize:13,lineHeight:1.55,whiteSpace:'pre-wrap',
              maxHeight:300,overflowY:'auto',
            }}>
              {item.userPrompt}
            </div>
          </div>
        ) : (
          <div style={{fontSize:13,color:'var(--fg-muted)',fontStyle:'italic',padding:12,background:'var(--bg-alt)',borderRadius:8}}>
            No originating prompt captured for this artefact. (Older artefacts from before provenance was tracked won't have one.)
          </div>
        )}
        <div style={{display:'flex',gap:8,justifyContent:'flex-end',marginTop:14,flexWrap:'wrap'}}>
          {item.chatId && (
            <a href={`#chat`} className="btn-ghost" style={{padding:'5px 12px',fontSize:12,textDecoration:'none'}}>
              Open chat
            </a>
          )}
          {item.intentId && (
            <a href={`#agents`} className="btn-ghost" style={{padding:'5px 12px',fontSize:12,textDecoration:'none'}}>
              Open agent run
            </a>
          )}
          {item.userPrompt && (
            <button
              className="btn-ghost"
              style={{padding:'5px 12px',fontSize:12}}
              onClick={async () => {
                try { await navigator.clipboard.writeText(item.userPrompt); } catch { /* ignore */ }
              }}
            >
              Copy prompt
            </button>
          )}
          <button className="btn-orange" style={{padding:'5px 12px',fontSize:12}} onClick={onClose}>Close</button>
        </div>
      </div>
    </div>
  );
};

const ReportPreviewModal = ({ path, onClose }) => {
  const [file, setFile] = React.useState(null);
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState(null);

  React.useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    getOrgRepoFile(path)
      .then(f => { if (!cancelled) setFile(f); })
      .catch(err => { if (!cancelled) setError(err.message); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [path]);

  return (
    <div className="modal-mask" onClick={onClose}>
      <div className="modal" onClick={e => e.stopPropagation()} style={{position:'relative',maxWidth:920,width:'95%'}}>
        <button className="modal__close" onClick={onClose}>×</button>
        <div style={{fontSize:11,fontFamily:'JetBrains Mono',color:'var(--fg-muted)',marginBottom:6}}>{path}</div>
        {loading && <div style={{padding:40,textAlign:'center',color:'var(--fg-muted)'}}>Loading…</div>}
        {error && <ErrorBanner>{error}</ErrorBanner>}
        {file && (
          <>
            <h3 style={{marginBottom:10}}>{fileTitle(file)}</h3>
            <div style={{fontSize:11,color:'var(--fg-muted)',marginBottom:14,fontFamily:'JetBrains Mono'}}>
              sha {file.sha?.slice(0,7)} · {approxSize(file.content)}
            </div>
            <pre style={{whiteSpace:'pre-wrap',wordBreak:'break-word',fontFamily:'JetBrains Mono',fontSize:12.5,lineHeight:1.6,background:'var(--bg-alt)',padding:18,borderRadius:8,maxHeight:'60vh',overflow:'auto'}}>
              {file.content}
            </pre>
          </>
        )}
      </div>
    </div>
  );
};
